/**
 * AdminAccounting v3 — Financial Management
 * KPI cards + 9 tabs: Overview, Transactions, Banks, Installments,
 *                     Overdue, Notes, Expenses, Refunds, Interest Settings
 *
 * New in v3 (muhasebe-3-refunds):
 *  - Expenses tab: CRUD + category summary
 *  - Refunds tab: form with file upload, Moka phase-2 notice
 *  - Interest Settings tab: singleton settings + manual job trigger
 *  - Dashboard KPI: Gross / Refunded / Net Income / Net Profit / Overdue count / Interest count
 *  - Installments: interest / status badge extensions (overdue_warn, interest, legal)
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { fmtTRY } from '../utils/formatCurrency';
import {
  Plus, X, TrendingUp, TrendingDown, DollarSign, Landmark,
  AlertTriangle, FileText, CreditCard, Check, Trash2, Pencil,
  RotateCcw, Settings, RefreshCw, Info, ArrowDownLeft, ArrowUpRight,
  Upload, Link2, Copy, CheckCircle2, BookOpen, ChevronLeft, ChevronRight,
  Wallet, Search,
} from 'lucide-react';
import axios from 'axios';
import {
  PageHeader,
  FilterBar,
  DataTable,
  DateRange,
  ConfirmDialog,
  EmptyState,
  LoadingState,
  StatusBadge,
  FilePicker,
} from '../components/admin';
import { promissoryStatusBadgeProps } from './AdminStudentHub/AccountingTab/columnDefs';
// FAZ 4 PAKET I: tahsilat uclarinin `error_code` sozlugu (accounting.errors.*)
// TEK yerde tutuluyor; bu sayfa da ayni sozlukten okur (kod tekrari yok).
import { noteErrorMessage } from './AdminStudentHub/AccountingTab/noteUiKit';
// POS hesabına tahsilatta "aktarılan tutar" zorunluluğu — üç giriş noktasında
// (CollectModal, NoteCollectModal, CollectionForm.jsx) AYNI kural; mantık tek
// yerde (bkz. dosya başı yorumu).
import { isSelectedBankPos, validateNetAmount, netAmountErrorI18nKey, computeCommission } from '../lib/posNetAmount';
// Moka United ödeme linki dialog'u — ayrı, yeni dosyada (bkz. proje-yoneticisi
// görev talimatı, 24 Eylül 2026: "kod YENİ dosyalarda olsun, mevcut dosyaya
// yalnız ince bağlama"). Bu satır + aşağıdaki state/buton/render 3 nokta dışında
// bu dosyaya dokunulmadı.
import MokaLinkDialog from '../components/admin/moka/MokaLinkDialog';
import { useMokaConfig } from '../api/mutations/moka';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Helpers ──────────────────────────────────────────────────────────────────
// fmtTRY artık ../utils/formatCurrency'den geliyor — "1.234,56 ₺" formatı
function fmtDate(val) {
  if (!val) return '—';
  try { return new Date(val).toLocaleDateString('tr-TR'); } catch { return val; }
}
const TODAY = new Date().toISOString().split('T')[0];

// ─── Mali İşler liste sekmeleri — sunucu taraflı pagination + zorunlu tarih ───
// (feature/mali-isler-liste-web, 2026-09-29) İlk açılışta liste GELMEZ; kullanıcı
// tarih aralığı seçip "Listele"ye basana kadar hiçbir sorgu atılmaz. Desen
// AdminMuhasebeListesi.jsx'teki appliedFilters/hasDateRange/requestSeq deseniyle
// BİREBİR aynı — bkz. o dosyanın başlık yorumu. Backend sözleşmesi: GET
// /admin/accounting/lists/{installments|overdue|promissory-notes|ledger|
// expenses|refunds} — date_from/date_to ZORUNLU (yoksa backend 400
// tarih_araligi_zorunlu döner), q opsiyonel arama, skip/limit sayfalama.
// Yanıt: {items, total, skip, limit, truncated}. items'in alan şekli ESKİ
// uçlarla (/admin/installments, /admin/installments/overdue, /admin/
// promissory-notes, /admin/accounting/ledger, /admin/expenses, /admin/refunds)
// AYNI — mevcut satır render'i (installmentCols, noteCols, expenseCols,
// refundCols…) DEĞİŞMEDEN kullanılabiliyor.
const LIST_LIMIT = 50;

function toIsoDate(date) {
  if (!date) return undefined;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Tek liste ucu için: appliedFilters null iken hiç fetch atmaz (mount'ta
// otomatik sorgu YOK); "Listele" `applyFilters(filters)` çağırınca 1. sayfadan
// başlar. `onSuccess` ref üzerinden tutulur — caller'ın her render'da yeni bir
// inline fonksiyon geçmesi fetchPage'i (ve dolayısıyla useEffect'i) YENİDEN
// TETİKLEMEZ (sonsuz döngü riski yok).
function useAccountingList({ endpoint, authHeaders, lang, toast, onSuccess }) {
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const requestSeq = useRef(0);
  const onSuccessRef = useRef(onSuccess);
  useEffect(() => { onSuccessRef.current = onSuccess; });

  const fetchPage = useCallback((filters, currentPage) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(false);
    const params = { ...filters, skip: (currentPage - 1) * LIST_LIMIT, limit: LIST_LIMIT };
    axios.get(`${API}/admin/accounting/lists/${endpoint}`, { headers: authHeaders(), params })
      .then((res) => {
        if (seq !== requestSeq.current) return;
        setRows(res.data?.items || []);
        setTotal(res.data?.total || 0);
        setTruncated(!!res.data?.truncated);
        onSuccessRef.current?.(res.data);
      })
      .catch((err) => {
        if (seq !== requestSeq.current) return;
        setError(true);
        setRows([]);
        setTotal(0);
        toast.error(getApiErrorMessage(err, lang));
      })
      .finally(() => {
        if (seq !== requestSeq.current) return;
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, authHeaders, lang]);

  useEffect(() => {
    if (appliedFilters === null) return;
    fetchPage(appliedFilters, page);
  }, [appliedFilters, page, fetchPage]);

  const applyFilters = useCallback((filters) => {
    setPage(1);
    setAppliedFilters(filters);
  }, []);

  // Mutasyon (oluşturma/düzenleme/tahsilat) sonrası: yalnızca daha önce bir
  // "Listele" uygulanmışsa aynı filtrelerle yeniden çek — hiç arama
  // yapılmamışsa (appliedFilters === null) bir şey YAPMA.
  const refetchIfApplied = useCallback(() => {
    if (appliedFilters !== null) fetchPage(appliedFilters, page);
  }, [appliedFilters, page, fetchPage]);

  return {
    rows, total, loading, error, truncated, page, setPage, appliedFilters,
    hasSearched: appliedFilters !== null, applyFilters, refetchIfApplied,
  };
}

// ─── Tarih aralığı + arama + "Listele" araç çubuğu (6 liste sekmesinde ortak) ──
// Enter tuşuyla da tetiklenmesi için <form onSubmit> sarmalıyor — bilinçli
// tercih: FilterBar bileşeni onKeyDown/onSubmit geçirmiyor, bu yüzden burada
// ayrı, sade bir arama input'u kullanılıyor (kapsam dışı: components/admin
// düzenlenmedi).
function ListToolbar({ dateRange, onDateRangeChange, search, onSearchChange, onListele, hasDateRange, loading, t, children }) {
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onListele(); }}
      className="flex flex-wrap items-end gap-3 mb-4"
    >
      <div className="min-w-[240px]">
        <p className="text-[11px] font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1.5">{t('admin.acc_list_date_range')}</p>
        <DateRange value={dateRange} onChange={onDateRangeChange} shortcuts={['today', 'last7', 'last30', 'thisMonth', 'lastMonth']} />
      </div>
      <div className="flex-1 min-w-[200px] max-w-[320px]">
        <p className="text-[11px] font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1.5">{t('admin.search_placeholder')}</p>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('admin.search_placeholder')}
            className="w-full pl-9 pr-3 h-9 rounded-xl border-2 border-[var(--border-light)] focus:border-[var(--brand-primary)] bg-white text-sm"
          />
        </div>
      </div>
      {children}
      <button type="submit" disabled={loading || !hasDateRange} className="btn-primary h-9 px-5 text-xs flex items-center gap-1.5 disabled:opacity-50">
        {loading ? <RefreshCw size={14} className="animate-spin" /> : <Search size={14} />}
        {t('admin.acc_list_btn_list')}
      </button>
      {!hasDateRange && (
        <p className="text-[11px] font-semibold text-amber-600 flex items-center gap-1 basis-full">
          <AlertTriangle size={13} className="shrink-0" />
          {t('admin.acc_list_date_required')}
        </p>
      )}
    </form>
  );
}

function TruncatedWarning({ t }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 mb-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold">
      <AlertTriangle size={14} className="shrink-0" />
      {t('admin.acc_list_truncated_warning')}
    </div>
  );
}

// ─── Bakiye tonu (FAZ 1 hizalama) ─────────────────────────────────────────────
// AdminStudentHub/AccountingTab/DebtLedgerSubTab.jsx:346-356'daki üç durumlu
// (amber/emerald/rose) desenin bu sayfadaki karşılığı. ÖNEMLİ FARK: orada
// negatif bakiye "öğrenci alacaklı" anlamına gelir ve etiket
// "Öğrenciye İade Borcu" olur. BURADA (banka/kasa bakiyesi ve birleşik defter
// net akışı) negatif değer bir ÖĞRENCİ ALACAĞI DEĞİL — kurumun kendi nakit
// pozisyonunun eksiye düşmesi (kasa açığı / kayıtlı çıkış > kayıtlı giriş).
// Bu yüzden etiket "İade Borcu" yerine ayrı, duruma özgü rozet/ipucu metniyle
// verilir (bkz. admin.acc_balance_deficit_badge /
// admin.acc_ledger_balance_deficit_hint) — ton mantığı ortak, kelime farklı.
function balanceToneClass(value) {
  if (value > 0) return 'text-[var(--brand-primary)]';
  if (value === 0) return 'text-emerald-600';
  return 'text-rose-600';
}

// ─── Ledger tip rozeti renkleri ───────────────────────────────────────────────
function ledgerTypeVariant(type) {
  if (type === 'gelir') return 'success';
  if (type === 'gider') return 'danger';
  if (type === 'iade') return 'warning';
  if (type === 'havale_giris') return 'success';
  if (type === 'havale_cikis') return 'danger';
  return 'neutral';
}

// ─── Banka/kasa hesap türü rozeti (cash | bank | pos) ─────────────────────────
function accountTypeVariant(type) {
  if (type === 'cash') return 'success';
  if (type === 'pos') return 'warning';
  return 'primary'; // bank (varsayılan)
}
function accountTypeIcon(type) {
  if (type === 'cash') return Wallet;
  if (type === 'pos') return CreditCard;
  return Landmark; // bank (varsayılan)
}
function accountTypeLabel(type, t) {
  if (type === 'cash') return t('admin.acc_account_type_cash');
  if (type === 'pos') return t('admin.acc_account_type_pos');
  return t('admin.acc_account_type_bank'); // bank (varsayılan)
}

// ─── BankStatementModal ───────────────────────────────────────────────────────
function BankStatementModal({ bankId, bankName, authHeaders, t, toast, lang, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const LIMIT = 30;

  const fetchStatement = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const params = { skip: page * LIMIT, limit: LIMIT };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/banks/${bankId}/statement`,
        { headers: authHeaders(), params }
      );
      setData(res.data);
    } catch (err) {
      setError(true);
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  }, [bankId, authHeaders, lang, toast, dateFrom, dateTo, page]);

  useEffect(() => { fetchStatement(); }, [fetchStatement]);

  const entries = data?.entries || [];
  const bank = data?.bank || {};

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('admin.acc_statement_title')}
    >
      <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Başlık */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          <div>
            <h2 className="font-bold text-base flex items-center gap-2">
              <Landmark size={18} className="text-[var(--brand-primary)]" />
              {bankName} — {t('admin.acc_statement_title')}
            </h2>
            {bank.iban && (
              <p className="text-[11px] font-mono text-[var(--text-muted)] mt-0.5">IBAN: {bank.iban}</p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label={t('admin.btn_close')}
            className="p-1.5 rounded-xl text-[var(--text-muted)] hover:bg-gray-100 transition"
          >
            <X size={18} />
          </button>
        </div>

        {/* Bakiye kartı */}
        {data && (
          <div className="flex items-center gap-6 px-5 py-3 bg-gray-50 border-b border-gray-100 shrink-0 flex-wrap gap-y-2">
            {bank.initial_balance !== undefined && (
              <div className="text-center">
                <p className="text-[10px] font-bold text-[var(--text-muted)] uppercase">{t('admin.acc_statement_opening')}</p>
                <p className={`font-bold text-sm ${balanceToneClass(bank.initial_balance)}`}>{fmtTRY(bank.initial_balance)}</p>
                {bank.initial_balance < 0 && (
                  <p className="text-[9px] font-bold uppercase text-rose-600">{t('admin.acc_balance_deficit_badge')}</p>
                )}
              </div>
            )}
            <div className="text-center">
              <p className="text-[10px] font-bold text-[var(--text-muted)] uppercase">{t('admin.acc_statement_balance')}</p>
              <p className={`font-bold text-lg ${balanceToneClass(data.final_balance)}`}>{fmtTRY(data.final_balance)}</p>
              {data.final_balance < 0 && (
                <p className="text-[9px] font-bold uppercase text-rose-600">{t('admin.acc_balance_deficit_badge')}</p>
              )}
            </div>
          </div>
        )}

        {/* Filtreler */}
        <div className="flex gap-2 px-5 py-3 border-b border-gray-100 shrink-0 flex-wrap">
          <div className="flex items-center gap-1 text-xs">
            <label htmlFor="stmt-from" className="font-bold text-[var(--text-muted)] whitespace-nowrap">{t('admin.acc_ledger_from')}:</label>
            <input
              id="stmt-from"
              type="date"
              value={dateFrom}
              onChange={e => { setPage(0); setDateFrom(e.target.value); }}
              className="px-2 py-1 rounded-lg border-2 border-[var(--border-light)] bg-white text-xs"
            />
          </div>
          <div className="flex items-center gap-1 text-xs">
            <label htmlFor="stmt-to" className="font-bold text-[var(--text-muted)] whitespace-nowrap">{t('admin.acc_ledger_to')}:</label>
            <input
              id="stmt-to"
              type="date"
              value={dateTo}
              onChange={e => { setPage(0); setDateTo(e.target.value); }}
              className="px-2 py-1 rounded-lg border-2 border-[var(--border-light)] bg-white text-xs"
            />
          </div>
        </div>

        {/* İçerik */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading ? (
            <div className="py-8 text-center text-sm text-[var(--text-muted)]">
              <RefreshCw size={24} className="mx-auto mb-2 animate-spin text-[var(--brand-primary)]" />
              {t('admin.acc_ledger_loading')}
            </div>
          ) : error ? (
            <div className="py-8 text-center text-red-500">
              <AlertTriangle size={32} className="mx-auto mb-2" />
              <p className="font-semibold text-sm">{t('admin.acc_ledger_error')}</p>
              <button onClick={fetchStatement} className="mt-3 px-4 py-2 text-xs rounded-xl border-2 border-gray-200 font-bold">{t('admin.acc_retry')}</button>
            </div>
          ) : entries.length === 0 ? (
            <div className="py-8 text-center text-[var(--text-muted)]">
              <BookOpen size={32} className="mx-auto mb-2 opacity-40" />
              <p className="font-semibold text-sm">{t('admin.acc_ledger_empty')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-100">
              <table className="w-full text-xs" role="table">
                <thead>
                  <tr className="bg-gray-50 text-[var(--text-muted)] uppercase tracking-wide text-[10px]">
                    <th className="px-3 py-2 text-left font-semibold">{t('admin.acc_ledger_col_date')}</th>
                    <th className="px-3 py-2 text-left font-semibold">{t('admin.acc_ledger_col_type')}</th>
                    <th className="px-3 py-2 text-left font-semibold">{t('admin.acc_ledger_col_desc')}</th>
                    <th className="px-3 py-2 text-right font-semibold">{t('admin.acc_ledger_col_debit')}</th>
                    <th className="px-3 py-2 text-right font-semibold">{t('admin.acc_ledger_col_credit')}</th>
                    <th className="px-3 py-2 text-right font-semibold">{t('admin.acc_ledger_col_balance')}</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e, idx) => (
                    <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap text-[var(--text-muted)]">{fmtDate(e.date)}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-bold ${
                          ledgerTypeVariant(e.type) === 'success' ? 'bg-green-50 text-green-700' :
                          ledgerTypeVariant(e.type) === 'danger' ? 'bg-red-50 text-red-700' :
                          ledgerTypeVariant(e.type) === 'warning' ? 'bg-amber-50 text-amber-700' :
                          'bg-gray-50 text-gray-700'
                        }`}>{e.type || '—'}</span>
                      </td>
                      <td className="px-3 py-2 max-w-[200px] truncate text-[var(--text-muted)]">{e.description || '—'}</td>
                      <td className="px-3 py-2 text-right font-semibold text-red-600">
                        {e.direction === 'out' ? fmtTRY(e.amount) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-green-600">
                        {e.direction === 'in' ? fmtTRY(e.amount) : '—'}
                      </td>
                      <td className={`px-3 py-2 text-right font-bold ${e.running_balance < 0 ? 'text-rose-600' : ''}`}>{fmtTRY(e.running_balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Sayfalama */}
        {!loading && !error && (data?.total || 0) > LIMIT && (
          <div className="flex items-center justify-center gap-3 px-5 py-3 border-t border-gray-100 shrink-0 text-xs font-bold">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              aria-label={t('admin.acc_ledger_prev')}
              className="p-1.5 rounded-lg border-2 border-[var(--border-light)] disabled:opacity-40 hover:border-[var(--brand-primary)] transition"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="text-[var(--text-muted)]">{page + 1}</span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={(page + 1) * LIMIT >= (data?.total || 0)}
              aria-label={t('admin.acc_ledger_next')}
              className="p-1.5 rounded-lg border-2 border-[var(--border-light)] disabled:opacity-40 hover:border-[var(--brand-primary)] transition"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// B2 fix — terminal (tahsil edilemez, geri dönüşü olmayan) taksit statüleri.
// Backend SSOT: backend/services/installment_status.py::INSTALLMENT_TERMINAL_STATUSES
// (paid, cancelled) — burada AYNI iki değer literal olarak taşınır (backend
// modülü JS'e import edilemez). Yeni bir statü türetme mantığı İCAT EDİLMEDİ;
// bu liste yalnızca "Tahsil Et" butonunu ne zaman kapatacağımızı belirler,
// satırın kendisi zaten backend'den `/admin/installments`'ın döndüğü ham
// `status` alanını taşıyor (KPI'ın okuduğu PENDING_INSTALLMENT_MATCH ile
// aynı iki terminal değer).
const INSTALLMENT_TERMINAL_STATUSES = ['paid', 'cancelled'];

function installmentStatusVariant(status) {
  if (status === 'paid') return 'success';
  if (status === 'cancelled') return 'neutral';
  if (status === 'legal') return 'danger';
  if (status === 'interest') return 'warning';
  if (status === 'overdue_warn') return 'warning';
  if (status === 'overdue') return 'danger';
  return 'neutral';
}

function refundStatusVariant(status) {
  if (status === 'completed') return 'success';
  if (status === 'rejected') return 'danger';
  if (status === 'approved') return 'warning';
  return 'neutral';
}

// ─── CollectModal ──────────────────────────────────────────────────────────────
// FAZ 4 PAKET I — backend sozlesmesi sikilasti (routes/payments.py::collect_installment):
//   1. `bank_id` ZORUNLU + `db.banks` varlik/`is_active` dogrulamasi
//      (400 `taksit_tahsilat_banka_zorunlu` / `banka_bulunamadi`). Bos string
//      ile gonderilen para hicbir bankanin bakiyesine girmiyor ama ciroya
//      giriyordu; bu yuzden eski "Kasa" (bank_id="") secenegi KALDIRILDI —
//      nakit tahsilat icin de `banks` koleksiyonunda bir kasa kaydi secilir.
//      Desen NoteCollectModal (A0-5) ile birebir ayni.
//   2. Tahsilat tutari taksidin (faiz dahil) tutarina +-0.005 ESIT olmak
//      zorunda (400 `taksit_tahsilat_tutari_eslesmiyor`). Kismi tahsilat
//      kapsam disi (S-9 ile ertelendi) -> tutar alani SALT OKUNUR.
//      `readOnly` + `aria-readonly` kullaniliyor, `disabled` DEGIL: alan
//      klavye odagini korur (WCAG 2.1 AA), degeri okunabilir/kopyalanabilir.
export function CollectModal({ installment, banks, saving, onCollect, onClose, t }) {
  const lockedAmount = Number(installment?.amount_with_interest || installment?.amount || 0);
  const [form, setForm] = useState({
    amount: lockedAmount,
    payment_method: 'cash',
    bank_id: '',
    // B5 fix — "Hesaba Geçen Tutar" (net_amount). Ham string tutulur (input
    // value'su), sayıya çevirme/normalize (boş -> null) AdminAccounting.jsx'in
    // doCollectInstallment fonksiyonunda yapılır — CollectionForm.jsx (öğrenci
    // hub referans uygulaması) ile AYNI türetme kuralı: opsiyonel, >= 0,
    // <= brüt tutar + 0.005. Nakit/havale gibi kesintisiz yöntemlerde alan
    // boş bırakılır -> backend'e net_amount: null gider -> davranış DEĞİŞMEZ.
    net_amount: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const bankMissing = !form.bank_id;
  const netAmountRaw = form.net_amount;
  // POS hesabına tahsilatta aktarılan tutar zorunlu; kasa/banka/türsüz
  // hesapta opsiyonel kalır (bkz. lib/posNetAmount.js).
  const isPosAccount = isSelectedBankPos(banks, form.bank_id);
  const netAmountErrorKey = validateNetAmount({ amount: lockedAmount, netAmountRaw, isPos: isPosAccount });
  const netAmountInvalid = netAmountErrorKey !== null;
  const commission = computeCommission(lockedAmount, netAmountRaw);
  if (!installment) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card-clay p-5 max-w-md w-full">
        <h3 className="font-bold mb-1">{t('admin.acc_collect_payment')}</h3>
        <p className="text-sm text-[var(--text-muted)] mb-3">
          {installment.user_name} — {t('admin.acc_col_installment_no')} {installment.installment_no}
        </p>
        {(installment.interest_amount > 0) && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-2 mb-3 text-xs text-amber-700 flex items-center gap-2">
            <Info size={14} />
            {t('admin.acc_col_interest')}: {fmtTRY(installment.interest_amount)} — {t('admin.acc_col_total_due')}: {fmtTRY(installment.amount_with_interest)}
          </div>
        )}
        <div className="space-y-2 mb-4">
          <div>
            <label htmlFor="collect-amount" className="text-xs font-bold block mb-0.5">{t('admin.acc_col_amount')}</label>
            <input
              id="collect-amount"
              type="number"
              value={form.amount}
              readOnly
              aria-readonly="true"
              aria-describedby="collect-amount-hint"
              data-testid="collect-amount-input"
              className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm"
            />
            <p id="collect-amount-hint" className="text-[11px] text-[var(--text-muted)] mt-0.5">
              {t('admin.acc_collect_amount_locked_hint')}
            </p>
          </div>
          <div>
            <label htmlFor="collect-method" className="text-xs font-bold block mb-0.5">{t('admin.acc_refund_col_method')}</label>
            <select
              id="collect-method"
              value={form.payment_method}
              onChange={e => setForm({ ...form, payment_method: e.target.value })}
              className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm"
            >
              <option value="cash">{t('admin.acc_cash')}</option>
              <option value="credit_card">{t('admin.acc_card')}</option>
              <option value="bank_transfer">{t('admin.acc_transfer')}</option>
            </select>
          </div>
          <div>
            <label htmlFor="collect-bank" className="text-xs font-bold block mb-0.5">
              {t('admin.acc_banks')} <span className="text-red-500">*</span>
            </label>
            <select
              id="collect-bank"
              value={form.bank_id}
              aria-required="true"
              aria-invalid={submitted && bankMissing ? true : undefined}
              aria-describedby={submitted && bankMissing ? 'collect-bank-error' : undefined}
              onChange={e => setForm({ ...form, bank_id: e.target.value })}
              data-testid="collect-bank-select"
              className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm"
            >
              <option value="">{t('admin.acc_note_collect_bank_placeholder')}</option>
              {banks.map(b => <option key={b.bank_id} value={b.bank_id}>{b.name}</option>)}
            </select>
            {submitted && bankMissing && (
              <p id="collect-bank-error" role="alert" data-testid="collect-bank-error" className="text-[11px] font-bold text-rose-600 mt-0.5">
                {t('admin.acc_note_collect_bank_required')}
              </p>
            )}
          </div>
          {/* B5 fix — "Hesaba Geçen Tutar" (net_amount). CollectionForm.jsx (öğrenci
              hub referans uygulaması) ile AYNI i18n metinleri/yardım cümlesi — ikinci
              bir metin kopyası icat edilmedi, mevcut sözlük anahtarları okundu.
              POS hesabına tahsilatta zorunlu hale gelir (bkz. lib/posNetAmount.js). */}
          <div>
            <label htmlFor="collect-net-amount" className="text-xs font-bold block mb-0.5">
              {isPosAccount ? t('accounting.collect_form.net_settled_amount_required_label') : t('accounting.fields.net_settled_amount.label')}
              {isPosAccount && <span className="text-red-500"> *</span>}
            </label>
            <input
              id="collect-net-amount"
              type="number"
              step="0.01"
              min="0"
              value={form.net_amount}
              onChange={e => setForm({ ...form, net_amount: e.target.value })}
              aria-required={isPosAccount ? 'true' : undefined}
              aria-invalid={submitted && netAmountInvalid ? true : undefined}
              aria-describedby="collect-net-amount-hint"
              data-testid="collect-net-amount-input"
              className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm"
            />
            {isPosAccount ? (
              <p id="collect-net-amount-hint" className="text-[11px] text-[var(--text-muted)] mt-0.5">
                {t('accounting.collect_form.net_settled_amount_help')}{' '}
                <strong className="font-bold text-[var(--text-main)]">
                  {t('accounting.collect_form.net_settled_amount_help_emphasis')}
                </strong>
              </p>
            ) : (
              <p id="collect-net-amount-hint" className="text-[11px] text-[var(--text-muted)] mt-0.5">
                {t('accounting.collect_form.net_settled_amount_optional_hint')}
              </p>
            )}
            {isPosAccount && commission !== null && (
              <p data-testid="collect-net-amount-commission" className="text-[11px] font-bold text-amber-700 mt-0.5">
                {t('accounting.collect_form.commission_label')}: {fmtTRY(commission)}
              </p>
            )}
            {submitted && netAmountInvalid && (
              <p role="alert" data-testid="collect-net-amount-error" className="text-[11px] font-bold text-rose-600 mt-0.5">
                {t(netAmountErrorI18nKey(netAmountErrorKey))}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { setSubmitted(true); if (!bankMissing && !netAmountInvalid) onCollect(installment.installment_id, form); }}
            disabled={saving}
            data-testid="collect-submit"
            className="btn-success px-6 py-2 text-sm flex-1 disabled:opacity-50 min-h-[44px]"
          >
            {saving ? t('admin.acc_collecting') : t('admin.acc_collect')}
          </button>
          <button onClick={onClose} disabled={saving} aria-label={t('admin.btn_close')} className="px-4 py-2 text-sm text-[var(--text-muted)] disabled:opacity-50 min-h-[44px]">
            {t('admin.btn_cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── NoteCollectModal ──────────────────────────────────────────────────────────
// A0-5 fix: senet tahsilatında bank_id zorunlu — bankasız tahsilat banka bakiyesine
// yansımıyordu (ciro artıyor, bakiye artmıyordu).
// POS aktarılan tutar fix: CollectModal ile AYNI kural — POS hesabına
// tahsilatta net_amount zorunlu, kasa/banka/türsüzde opsiyonel (bkz.
// lib/posNetAmount.js).
export function NoteCollectModal({ note, banks, saving, onCollect, onClose, t }) {
  const [form, setForm] = useState({ bank_id: '', net_amount: '' });
  const [submitted, setSubmitted] = useState(false);
  const bankMissing = !form.bank_id;
  const noteAmount = Number(note?.amount || 0);
  const isPosAccount = isSelectedBankPos(banks, form.bank_id);
  const netAmountErrorKey = validateNetAmount({ amount: noteAmount, netAmountRaw: form.net_amount, isPos: isPosAccount });
  const netAmountInvalid = netAmountErrorKey !== null;
  const commission = computeCommission(noteAmount, form.net_amount);
  if (!note) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card-clay p-5 max-w-md w-full">
        <h3 className="font-bold mb-1">{t('admin.acc_note_collect_modal_title')}</h3>
        <p className="text-sm text-[var(--text-muted)] mb-3">
          {note.user_name} — {note.note_number || (
            <span className="italic">{t('accounting.promissory_notes.no_number')}</span>
          )} — {fmtTRY(note.amount)}
        </p>
        <div className="space-y-2 mb-4">
          <div>
            <label htmlFor="note-collect-bank" className="text-xs font-bold block mb-0.5">
              {t('admin.acc_banks')} <span className="text-red-500">*</span>
            </label>
            <select
              id="note-collect-bank"
              value={form.bank_id}
              aria-required="true"
              aria-invalid={submitted && bankMissing ? true : undefined}
              aria-describedby={submitted && bankMissing ? 'note-collect-bank-error' : undefined}
              onChange={e => setForm({ ...form, bank_id: e.target.value })}
              data-testid="note-collect-bank-select"
              className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm"
            >
              <option value="">{t('admin.acc_note_collect_bank_placeholder')}</option>
              {banks.map(b => <option key={b.bank_id} value={b.bank_id}>{b.name}</option>)}
            </select>
            {submitted && bankMissing && (
              <p id="note-collect-bank-error" role="alert" data-testid="note-collect-bank-error" className="text-[11px] font-bold text-rose-600 mt-0.5">
                {t('admin.acc_note_collect_bank_required')}
              </p>
            )}
          </div>
          {/* POS hesabına tahsilatta zorunlu, aksi halde opsiyonel — CollectModal
              ile AYNI davranış/i18n anahtarları. */}
          <div>
            <label htmlFor="note-collect-net-amount" className="text-xs font-bold block mb-0.5">
              {isPosAccount ? t('accounting.collect_form.net_settled_amount_required_label') : t('accounting.fields.net_settled_amount.label')}
              {isPosAccount && <span className="text-red-500"> *</span>}
            </label>
            <input
              id="note-collect-net-amount"
              type="number"
              step="0.01"
              min="0"
              value={form.net_amount}
              onChange={e => setForm({ ...form, net_amount: e.target.value })}
              aria-required={isPosAccount ? 'true' : undefined}
              aria-invalid={submitted && netAmountInvalid ? true : undefined}
              aria-describedby="note-collect-net-amount-hint"
              data-testid="note-collect-net-amount-input"
              className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm"
            />
            {isPosAccount ? (
              <p id="note-collect-net-amount-hint" className="text-[11px] text-[var(--text-muted)] mt-0.5">
                {t('accounting.collect_form.net_settled_amount_help')}{' '}
                <strong className="font-bold text-[var(--text-main)]">
                  {t('accounting.collect_form.net_settled_amount_help_emphasis')}
                </strong>
              </p>
            ) : (
              <p id="note-collect-net-amount-hint" className="text-[11px] text-[var(--text-muted)] mt-0.5">
                {t('accounting.collect_form.net_settled_amount_optional_hint')}
              </p>
            )}
            {isPosAccount && commission !== null && (
              <p data-testid="note-collect-net-amount-commission" className="text-[11px] font-bold text-amber-700 mt-0.5">
                {t('accounting.collect_form.commission_label')}: {fmtTRY(commission)}
              </p>
            )}
            {submitted && netAmountInvalid && (
              <p role="alert" data-testid="note-collect-net-amount-error" className="text-[11px] font-bold text-rose-600 mt-0.5">
                {t(netAmountErrorI18nKey(netAmountErrorKey))}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { setSubmitted(true); if (!bankMissing && !netAmountInvalid) onCollect(note.note_id, form); }}
            disabled={saving}
            data-testid="note-collect-submit"
            className="btn-success px-6 py-2 text-sm flex-1 disabled:opacity-50 min-h-[44px]"
          >
            {saving ? t('admin.acc_collecting') : t('admin.acc_collect')}
          </button>
          <button onClick={onClose} disabled={saving} aria-label={t('admin.btn_close')} className="px-4 py-2 text-sm text-[var(--text-muted)] disabled:opacity-50 min-h-[44px]">
            {t('admin.btn_cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

// CATEGORIES component dışında — re-render'da yeniden oluşturulmuyor
const EXPENSE_CATEGORY_KEYS = [
  'rent', 'bank_fee', 'salary', 'utility', 'marketing', 'tax', 'office', 'other',
];

// ─── ExpenseForm ───────────────────────────────────────────────────────────────
function ExpenseForm({ initial, banks, saving, onSave, onClose, t }) {
  const [form, setForm] = useState(initial || {
    category: 'other', description: '', amount: 0,
    date: TODAY, bank_id: '', vendor: '', document_ref: '', is_recurring: false,
  });

  const catLabel = (key) => t(`admin.acc_cat_${key}`);

  return (
    <div className="card-clay p-4 mb-4 space-y-3">
      <div className="flex justify-between">
        <h3 className="font-bold text-sm">{initial ? t('admin.acc_expense_edit') : t('admin.acc_expense_new')}</h3>
        <button onClick={onClose} aria-label={t('admin.btn_close')}><X size={16} /></button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <div>
          <label htmlFor="exp-category" className="text-[10px] font-bold block mb-0.5">{t('admin.acc_expense_category')}</label>
          <select id="exp-category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
            className="w-full px-2 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-xs">
            {EXPENSE_CATEGORY_KEYS.map(v => <option key={v} value={v}>{catLabel(v)}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="exp-desc" className="text-[10px] font-bold block mb-0.5">{t('admin.acc_col_desc')} <span className="text-red-500">*</span></label>
          <input id="exp-desc" placeholder={t('admin.acc_col_desc')} value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            className="w-full px-2 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-xs" />
        </div>
        <div>
          <label htmlFor="exp-amount" className="text-[10px] font-bold block mb-0.5">{t('admin.acc_col_amount')} <span className="text-red-500">*</span></label>
          <input id="exp-amount" type="number" placeholder="TRY" value={form.amount}
            onChange={e => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })}
            className="w-full px-2 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-xs" />
        </div>
        <div>
          <label htmlFor="exp-date" className="text-[10px] font-bold block mb-0.5">{t('admin.acc_col_date')}</label>
          <input id="exp-date" type="date" value={form.date}
            onChange={e => setForm({ ...form, date: e.target.value })}
            className="w-full px-2 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-xs" />
        </div>
        <div>
          <label htmlFor="exp-bank" className="text-[10px] font-bold block mb-0.5">{t('admin.acc_banks')}</label>
          <select id="exp-bank" value={form.bank_id} onChange={e => setForm({ ...form, bank_id: e.target.value })}
            className="w-full px-2 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-xs">
            <option value="">{t('admin.acc_cash_register')}</option>
            {banks.map(b => <option key={b.bank_id} value={b.bank_id}>{b.name}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="exp-vendor" className="text-[10px] font-bold block mb-0.5">{t('admin.acc_expense_vendor')}</label>
          <input id="exp-vendor" placeholder={t('admin.acc_expense_vendor')} value={form.vendor}
            onChange={e => setForm({ ...form, vendor: e.target.value })}
            className="w-full px-2 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-xs" />
        </div>
        <div>
          <label htmlFor="exp-docref" className="text-[10px] font-bold block mb-0.5">{t('admin.acc_expense_doc_ref')}</label>
          <input id="exp-docref" placeholder={t('admin.acc_expense_doc_ref')} value={form.document_ref}
            onChange={e => setForm({ ...form, document_ref: e.target.value })}
            className="w-full px-2 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-xs" />
        </div>
        <label htmlFor="exp-recurring" className="flex items-center gap-2 text-xs font-semibold col-span-2 md:col-span-1 cursor-pointer">
          <input id="exp-recurring" type="checkbox" checked={form.is_recurring}
            onChange={e => setForm({ ...form, is_recurring: e.target.checked })}
            className="rounded" />
          {t('admin.acc_expense_recurring')}
        </label>
      </div>
      <button onClick={() => onSave(form)} disabled={saving} className="btn-success px-4 py-1.5 text-xs disabled:opacity-50">
        {saving ? t('admin.acc_saving') : t('admin.acc_save')}
      </button>
    </div>
  );
}

// ─── StudentSearchCombobox ───────────────────────────────────────────────────
// Paylaşılan öğrenci arama combobox'u (Tur 2'de RefundForm içinde doğdu, Tur 3'te
// (feature/mali-isler-ozet-tarih-web) HavaleRequestForm ve "Senet Ekle" formuna
// da taşındı — üçü de mount'ta parametresiz çekilen `/admin/student-ops`'un
// varsayılan page_size=20 sınırıyla (backend/routes/admin.py:3413) YALNIZ ilk 20
// öğrenciyi gösteriyordu. Arama tabanlı, 300ms debounce'lu: GET /admin/users?
// search=&role=student&limit=20 (routes/admin.py:105-118, services/
// admin_user_query.py::build_admin_user_filter — /admin/student-ops'un hızlı
// yolu ile AYNI projeksiyon, ADMIN_USER_LIST_PROJECTION). En az 2 karakterden
// itibaren arar. Kontrollü bileşen: `value` seçili user_id, `onChange(user_id)`
// ile parent'a bildirir — seçilen öğrencinin adını kendi içinde tutar (parent
// yalnızca id ile ilgilenir, `students` listesi gibi paylaşılan bir state'e
// ihtiyaç YOK). t/lang/authHeaders/toast kendi içinde context'ten okunur.
function StudentSearchCombobox({ id, value, onChange, placeholder }) {
  const { t, lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const [query, setQuery] = useState('');
  const [selectedName, setSelectedName] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const seqRef = useRef(0);

  // Parent formu resetlerse (value '' olursa) gösterilen isim de temizlensin.
  useEffect(() => { if (!value) setSelectedName(''); }, [value]);

  useEffect(() => {
    const q = query.trim();
    if (timerRef.current) clearTimeout(timerRef.current);
    if (q.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    timerRef.current = setTimeout(() => {
      const seq = ++seqRef.current;
      axios.get(`${API}/admin/users`, {
        headers: authHeaders(),
        params: { search: q, role: 'student', limit: 20 },
      })
        .then(res => {
          if (seq !== seqRef.current) return;
          setResults(res.data?.users || []);
        })
        .catch(err => {
          if (seq !== seqRef.current) return;
          setResults([]);
          toast.error(getApiErrorMessage(err, lang));
        })
        .finally(() => {
          if (seq !== seqRef.current) return;
          setSearching(false);
        });
    }, 300);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [query, authHeaders, lang, toast]);

  const clearSelection = () => {
    onChange('');
    setSelectedName('');
    setQuery('');
    setResults([]);
  };

  return (
    <div className="relative">
      <input
        id={id}
        type="text"
        autoComplete="off"
        value={value ? selectedName : query}
        onChange={e => {
          if (value) onChange('');
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => { if (!value) setOpen(true); }}
        onBlur={() => { setTimeout(() => setOpen(false), 150); }}
        placeholder={placeholder || t('admin.acc_refund_student_search_placeholder')}
        className="w-full px-2 py-2 pr-7 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-xs"
      />
      {value && (
        <button
          type="button"
          onClick={clearSelection}
          aria-label={t('admin.btn_close')}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-main)]"
        >
          <X size={13} />
        </button>
      )}
      {open && !value && query.trim().length >= 2 && (
        <div className="absolute z-10 mt-1 w-full max-h-52 overflow-y-auto rounded-xl border-2 border-[var(--border-light)] bg-white shadow-lg">
          {searching ? (
            <p className="px-3 py-2 text-xs text-[var(--text-muted)]">{t('admin.acc_refund_student_search_searching')}</p>
          ) : results.length === 0 ? (
            <p className="px-3 py-2 text-xs text-[var(--text-muted)]">{t('admin.acc_refund_student_search_no_results')}</p>
          ) : (
            results.map(s => (
              <button
                key={s.user_id}
                type="button"
                onMouseDown={e => e.preventDefault()}
                onClick={() => {
                  onChange(s.user_id);
                  setSelectedName(s.name);
                  setQuery('');
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 border-b border-gray-50 last:border-0"
              >
                <span className="font-semibold">{s.name}</span>
                {s.email && <span className="text-[var(--text-muted)] ml-1">({s.email})</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─── RefundForm ────────────────────────────────────────────────────────────────
// Öğrenci seçimi Tur 2'de arama combobox'una çevrildi, Tur 3'te ortak
// StudentSearchCombobox'a taşındı (yukarıdaki yorum bloğuna bkz.). `students`
// prop'u KALDIRILDI — bu bileşende kullanılmıyor.
function RefundForm({ banks, saving, onSave, onClose, t, authHeaders, toast, lang }) {
  const [form, setForm] = useState({
    user_id: '', amount: 0, reason: 'student_request', method: 'transfer',
    payment_id: '', invoice_id: '', bank_id: '',
  });
  const [receiptFile, setReceiptFile] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  // BUG #4 FIX: Dekont yalnızca havale/EFT (transfer) için zorunlu; nakit (cash) opsiyonel
  const needsReceipt = form.method === 'transfer';
  const isMoka = form.method === 'moka_refund';

  // Öğrenci seçilince faturalarını çek
  useEffect(() => {
    if (!form.user_id) {
      setInvoices([]);
      setForm(f => ({ ...f, invoice_id: '' }));
      return;
    }
    let cancelled = false;
    setLoadingInvoices(true);
    setInvoices([]);
    setForm(f => ({ ...f, invoice_id: '' }));
    axios.get(`${API}/admin/invoices`, {
      headers: authHeaders(),
      params: { user_id: form.user_id, limit: 200 },
    })
      .then(res => {
        if (!cancelled) setInvoices(res.data?.invoices || []);
      })
      .catch(err => {
        if (!cancelled) toast.error(getApiErrorMessage(err, lang));
      })
      .finally(() => {
        if (!cancelled) setLoadingInvoices(false);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.user_id]);

  const handleSave = () => {
    onSave(form, receiptFile);
  };

  return (
    <div className="card-clay p-4 mb-4 space-y-3">
      <div className="flex justify-between">
        <h3 className="font-bold text-sm">{t('admin.acc_refund_new')}</h3>
        <button onClick={onClose} aria-label={t('admin.btn_close')}><X size={16} /></button>
      </div>

      {isMoka && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-3 flex items-start gap-2 text-xs text-amber-700">
          <Info size={14} className="mt-0.5 shrink-0" />
          {t('admin.acc_refund_moka_notice')}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {/* BUG #1 FIX: user_id zorunlu — student seçimi eklendi. Tur 2/3: paylaşılan
            StudentSearchCombobox (dosya başındaki yorum bloğuna bkz.). */}
        <div className="col-span-2 md:col-span-3">
          <label htmlFor="refund-user-search" className="text-[10px] font-bold text-[var(--text-main)] block mb-0.5">
            {t('admin.acc_select_student')} <span className="text-red-500">*</span>
          </label>
          <StudentSearchCombobox
            id="refund-user-search"
            value={form.user_id}
            onChange={(userId) => setForm(f => ({ ...f, user_id: userId }))}
          />
        </div>

        <div>
          <label htmlFor="refund-amount" className="text-[10px] font-bold text-[var(--text-main)] block mb-0.5">
            {t('admin.acc_refund_amount')} <span className="text-red-500">*</span>
          </label>
          <input
            id="refund-amount"
            type="number"
            placeholder={t('admin.acc_refund_amount')}
            value={form.amount}
            onChange={e => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })}
            className="w-full px-2 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-xs"
          />
        </div>

        <div>
          <label htmlFor="refund-reason" className="text-[10px] font-bold text-[var(--text-main)] block mb-0.5">
            {t('admin.acc_refund_col_reason')}
          </label>
          <select
            id="refund-reason"
            value={form.reason}
            onChange={e => setForm({ ...form, reason: e.target.value })}
            className="w-full px-2 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-xs"
          >
            <option value="student_request">{t('admin.acc_reason_student_request')}</option>
            <option value="course_cancel">{t('admin.acc_reason_course_cancel')}</option>
            <option value="content_change">{t('admin.acc_reason_content_change')}</option>
            <option value="other">{t('admin.acc_reason_other')}</option>
          </select>
        </div>

        <div>
          <label htmlFor="refund-method" className="text-[10px] font-bold text-[var(--text-main)] block mb-0.5">
            {t('admin.acc_refund_col_method')}
          </label>
          <select
            id="refund-method"
            value={form.method}
            onChange={e => setForm({ ...form, method: e.target.value })}
            className="w-full px-2 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-xs"
          >
            <option value="transfer">{t('admin.acc_method_transfer')}</option>
            <option value="cash">{t('admin.acc_method_cash')}</option>
            <option value="moka_refund">{t('admin.acc_method_moka_refund')}</option>
          </select>
        </div>

        <div>
          <label htmlFor="refund-bank" className="text-[10px] font-bold text-[var(--text-main)] block mb-0.5">
            {t('admin.acc_cash_register')}
          </label>
          <select
            id="refund-bank"
            value={form.bank_id}
            onChange={e => setForm({ ...form, bank_id: e.target.value })}
            className="w-full px-2 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-xs"
          >
            <option value="">{t('admin.acc_cash_register')}</option>
            {banks.map(b => <option key={b.bank_id} value={b.bank_id}>{b.name}</option>)}
          </select>
        </div>

        {/* invoice_id — öğrencinin faturaları dropdown */}
        <div className="col-span-2 md:col-span-3">
          <label htmlFor="refund-invoice-id" className="text-[10px] font-bold text-[var(--text-main)] block mb-0.5">
            {t('admin.acc_refund_invoice_id')} <span className="text-red-500">*</span>
          </label>
          {loadingInvoices ? (
            <div className="w-full px-2 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 text-xs text-[var(--text-muted)] flex items-center gap-2">
              <RefreshCw size={12} className="animate-spin shrink-0" />
              {t('admin.acc_refund_invoice_loading')}
            </div>
          ) : (
            <select
              id="refund-invoice-id"
              value={form.invoice_id}
              onChange={e => setForm({ ...form, invoice_id: e.target.value })}
              disabled={!form.user_id}
              className="w-full px-2 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-xs disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {!form.user_id ? (
                <option value="">{t('admin.acc_refund_select_student_first')}</option>
              ) : invoices.length === 0 ? (
                <option value="">{t('admin.acc_refund_no_invoices')}</option>
              ) : (
                <>
                  <option value="">{t('admin.acc_refund_invoice_select')}</option>
                  {invoices.map(inv => (
                    <option key={inv.invoice_id} value={inv.invoice_id}>
                      {inv.invoice_no} — {fmtTRY(inv.grand_total ?? inv.total_amount)} ({t('admin.acc_refund_col_amount').toLowerCase()}: {fmtTRY(inv.remaining_amount)}) — {inv.status}
                    </option>
                  ))}
                </>
              )}
            </select>
          )}
        </div>

        <div>
          <label htmlFor="refund-payment-id" className="text-[10px] font-bold text-[var(--text-muted)] block mb-0.5">
            {t('admin.acc_refund_payment_id')} <span className="text-[10px] font-normal">({t('admin.acc_refund_optional')})</span>
          </label>
          <input
            id="refund-payment-id"
            placeholder={t('admin.acc_refund_payment_id')}
            value={form.payment_id}
            onChange={e => setForm({ ...form, payment_id: e.target.value })}
            className="w-full px-2 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-mono text-xs"
          />
        </div>
      </div>

      {needsReceipt && (
        <div>
          <p className="text-xs font-bold mb-1 text-[var(--text-main)]">
            {t('admin.acc_refund_receipt')}
            <span className="text-red-500 ml-1">*</span>
          </p>
          <FilePicker
            accept=".pdf,.jpg,.jpeg,.png"
            maxSizeMb={10}
            onFile={setReceiptFile}
            onClear={() => setReceiptFile(null)}
          />
        </div>
      )}

      <button onClick={handleSave} disabled={saving} className="btn-success px-4 py-1.5 text-xs disabled:opacity-50">
        {saving ? t('admin.acc_saving') : t('admin.acc_save')}
      </button>
    </div>
  );
}

// ─── InterestSettingsPanel ─────────────────────────────────────────────────────
function InterestSettingsPanel({ t, authHeaders, toast, lang }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [jobRunning, setJobRunning] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await axios.get(`${API}/admin/interest-settings`, { headers: authHeaders() });
      // BUG #3 FIX: Backend {"interest_settings": {...}} wrapper döndürüyor
      setSettings(res.data.interest_settings ?? res.data);
    } catch (err) {
      setLoadError(true);
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  }, [authHeaders, toast, lang]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/interest-settings`, settings, { headers: authHeaders() });
      toast.success(t('admin.acc_interest_saved'));
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSaving(false);
    }
  };

  const handleRunJob = async () => {
    setJobRunning(true);
    try {
      await axios.post(`${API}/admin/interest-settings/run-job`, {}, { headers: authHeaders() });
      toast.success(t('admin.acc_interest_job_run'));
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setJobRunning(false);
    }
  };

  const upd = (field, val) => setSettings(s => ({ ...s, [field]: val }));

  if (loading) return <LoadingState text={t('admin.acc_interest_loading')} />;
  // #9: loading'den AYRI error state
  if (loadError) return (
    <div className="card-clay p-6 text-center text-red-500">
      <AlertTriangle size={32} className="mx-auto mb-2" />
      <p className="font-semibold text-sm">{t('admin.acc_interest_load_error')}</p>
      <button onClick={fetchSettings} className="btn-secondary mt-3 px-4 py-2 text-xs">{t('admin.acc_retry')}</button>
    </div>
  );
  if (!settings) return <EmptyState icon={<Settings size={40} strokeWidth={1.5} />} title={t('admin.acc_interest_title')} />;

  const fieldCls = "w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm";

  return (
    <div className="space-y-4">
      {/* Run Job Banner */}
      <div className="card-clay p-4 border-l-4 border-l-amber-400 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex-1">
          <p className="font-bold text-sm flex items-center gap-2">
            <RefreshCw size={16} className="text-amber-500" />
            {t('admin.acc_interest_run_job')}
          </p>
          <p className="text-xs text-[var(--text-muted)] mt-0.5">{t('admin.acc_interest_run_job_desc')}</p>
        </div>
        {/* P1 #11 FIX: btn-warning tanımsız → btn-secondary */}
        <button
          onClick={handleRunJob}
          disabled={jobRunning}
          className="btn-secondary px-5 py-2 text-xs font-bold flex items-center gap-2 disabled:opacity-50 whitespace-nowrap"
        >
          <RefreshCw size={14} className={jobRunning ? 'animate-spin' : ''} />
          {jobRunning ? t('admin.acc_interest_job_running') : t('admin.acc_interest_run_job')}
        </button>
      </div>

      {/* Settings form */}
      <div className="card-clay p-5 space-y-4">
        <h4 className="font-bold text-sm flex items-center gap-2">
          <Settings size={16} className="text-[var(--brand-primary)]" />
          {t('admin.acc_interest_title')}
        </h4>

        {/* Enable toggle — P1 #7 FIX: tabIndex + onKeyDown (Space/Enter) */}
        <label className="flex items-center gap-3 cursor-pointer">
          <div
            role="switch"
            aria-checked={settings.enabled}
            tabIndex={0}
            onClick={() => upd('enabled', !settings.enabled)}
            onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); upd('enabled', !settings.enabled); } }}
            className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:ring-offset-2 ${settings.enabled ? 'bg-emerald-500' : 'bg-gray-300'}`}
          >
            <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${settings.enabled ? 'left-6' : 'left-1'}`} />
          </div>
          <span className="font-semibold text-sm">{t('admin.acc_interest_enabled')}</span>
        </label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="int-annual-rate" className="text-xs font-bold block mb-1">{t('admin.acc_interest_annual_rate')}</label>
            <input id="int-annual-rate" type="number" step="0.1" value={settings.annual_rate ?? ''} onChange={e => upd('annual_rate', parseFloat(e.target.value))} className={fieldCls} />
            <p className="text-[10px] text-[var(--text-muted)] mt-0.5">{t('admin.acc_interest_rate_hint')}</p>
          </div>
          <div>
            <label htmlFor="int-grace-period" className="text-xs font-bold block mb-1">{t('admin.acc_interest_grace_period')}</label>
            <input id="int-grace-period" type="number" value={settings.grace_period_days ?? ''} onChange={e => upd('grace_period_days', parseInt(e.target.value))} className={fieldCls} />
          </div>
          <div>
            <label htmlFor="int-warn-day-1" className="text-xs font-bold block mb-1">{t('admin.acc_interest_warning_day_1')}</label>
            <input id="int-warn-day-1" type="number" value={settings.warning_day_1 ?? ''} onChange={e => upd('warning_day_1', parseInt(e.target.value))} className={fieldCls} />
          </div>
          <div>
            <label htmlFor="int-warn-day-2" className="text-xs font-bold block mb-1">{t('admin.acc_interest_warning_day_2')}</label>
            <input id="int-warn-day-2" type="number" value={settings.warning_day_2 ?? ''} onChange={e => upd('warning_day_2', parseInt(e.target.value))} className={fieldCls} />
          </div>
          <div>
            <label htmlFor="int-start-day" className="text-xs font-bold block mb-1">{t('admin.acc_interest_start_day')}</label>
            <input id="int-start-day" type="number" value={settings.interest_start_day ?? ''} onChange={e => upd('interest_start_day', parseInt(e.target.value))} className={fieldCls} />
          </div>
          <div>
            <label htmlFor="int-legal-day" className="text-xs font-bold block mb-1">{t('admin.acc_interest_legal_day')}</label>
            <input id="int-legal-day" type="number" value={settings.legal_start_day ?? ''} onChange={e => upd('legal_start_day', parseInt(e.target.value))} className={fieldCls} />
          </div>
          <div>
            <label htmlFor="int-min-amount" className="text-xs font-bold block mb-1">{t('admin.acc_interest_min_amount')}</label>
            <input id="int-min-amount" type="number" step="0.01" value={settings.min_interest_amount ?? ''} onChange={e => upd('min_interest_amount', parseFloat(e.target.value))} className={fieldCls} />
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <button onClick={handleSave} disabled={saving} className="btn-primary px-6 py-2 text-xs disabled:opacity-50">
            {saving ? t('admin.acc_saving') : t('admin.acc_save')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── CopyButton ───────────────────────────────────────────────────────────────
function CopyButton({ text, t }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={handleCopy}
      aria-label={t('admin.acc_havale_copy')}
      className="ml-1 p-1 rounded hover:bg-gray-100 inline-flex items-center"
    >
      {copied ? <CheckCircle2 size={14} className="text-green-500" /> : <Copy size={14} className="text-[var(--text-muted)]" />}
    </button>
  );
}

// ─── HavaleRequestForm ─────────────────────────────────────────────────────────
// Admin-güdümlü havale talebi: POST /api/admin/payment/havale
// Dönüşte KO-STD referansı + banka bilgisi admin'e gösterilir (öğrenciye iletecek).
// Öğrenci seçimi Tur 3'te (feature/mali-isler-ozet-tarih-web) ortak
// StudentSearchCombobox'a çevrildi — `students` prop'u KALDIRILDI.
function HavaleRequestForm({ banks, saving, onSave, onClose, t }) {
  const [form, setForm] = useState({ user_id: '', course_id: '', amount: '', bank_id: '' });
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const { authHeaders } = useAuth();
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    setLoadingCourses(true);
    axios.get(`${API}/admin/courses`, { headers: authHeaders() })
      .then(res => {
        if (!cancelled) setCourses(res.data?.courses || res.data || []);
      })
      .catch(() => {
        if (!cancelled) setCourses([]);
      })
      .finally(() => { if (!cancelled) setLoadingCourses(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upd = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const fieldCls = "w-full px-2 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-xs";

  const handleSubmit = () => {
    if (!form.user_id) { toast.warning(t('admin.acc_validation_student')); return; }
    if (!form.course_id) { toast.warning(t('admin.acc_havale_val_course')); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.warning(t('admin.acc_validation_amount')); return; }
    const payload = {
      user_id: form.user_id,
      course_id: form.course_id,
      amount: parseFloat(form.amount),
    };
    if (form.bank_id) payload.bank_id = form.bank_id;
    onSave(payload);
  };

  return (
    <div className="card-clay p-4 mb-4 space-y-3 border-l-4 border-l-cyan-400">
      <div className="flex justify-between items-center">
        <h3 className="font-bold text-sm flex items-center gap-2">
          <Link2 size={16} className="text-cyan-500" />
          {t('admin.acc_havale_form_title')}
        </h3>
        <button onClick={onClose} aria-label={t('admin.btn_close')} className="p-1 rounded hover:bg-gray-100">
          <X size={16} />
        </button>
      </div>
      <p className="text-[10px] text-[var(--text-muted)]">{t('admin.acc_havale_form_desc')}</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="col-span-2">
          <label htmlFor="hav-student" className="text-[10px] font-bold block mb-0.5">
            {t('admin.acc_select_student')} <span className="text-red-500">*</span>
          </label>
          <StudentSearchCombobox
            id="hav-student"
            value={form.user_id}
            onChange={(userId) => upd('user_id', userId)}
          />
        </div>

        <div className="col-span-2">
          <label htmlFor="hav-course" className="text-[10px] font-bold block mb-0.5">
            {t('admin.acc_havale_course')} <span className="text-red-500">*</span>
          </label>
          {loadingCourses ? (
            <div className={`${fieldCls} flex items-center gap-2 text-[var(--text-muted)]`}>
              <RefreshCw size={12} className="animate-spin shrink-0" />
              {t('admin.acc_havale_courses_loading')}
            </div>
          ) : (
            <select
              id="hav-course"
              value={form.course_id}
              onChange={e => upd('course_id', e.target.value)}
              className={fieldCls}
            >
              <option value="">{t('admin.acc_havale_course_select')}</option>
              {courses.map(c => (
                <option key={c.course_id || c._id} value={c.course_id || c._id}>
                  {c.title || c.name}
                </option>
              ))}
            </select>
          )}
        </div>

        <div>
          <label htmlFor="hav-amount" className="text-[10px] font-bold block mb-0.5">
            {t('admin.acc_col_amount')} <span className="text-red-500">*</span>
          </label>
          <input
            id="hav-amount"
            type="number"
            step="0.01"
            min="0"
            value={form.amount}
            onChange={e => upd('amount', e.target.value)}
            placeholder="TRY"
            className={fieldCls}
          />
        </div>

        <div>
          <label htmlFor="hav-bank" className="text-[10px] font-bold block mb-0.5">
            {t('admin.acc_banks')} <span className="text-[10px] font-normal">({t('admin.acc_havale_bank_optional')})</span>
          </label>
          <select
            id="hav-bank"
            value={form.bank_id}
            onChange={e => upd('bank_id', e.target.value)}
            className={fieldCls}
          >
            <option value="">{t('admin.acc_havale_bank_auto')}</option>
            {banks.map(b => <option key={b.bank_id} value={b.bank_id}>{b.name}</option>)}
          </select>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="btn-primary px-4 py-1.5 text-xs disabled:opacity-50 flex items-center gap-1 min-h-[36px]"
        >
          {saving ? t('admin.acc_havale_creating') : t('admin.acc_havale_create')}
        </button>
        <button onClick={onClose} disabled={saving} className="px-4 py-1.5 text-xs rounded-xl border-2 border-[var(--border-light)] font-bold disabled:opacity-50">
          {t('admin.btn_cancel')}
        </button>
      </div>
    </div>
  );
}

// ─── HavaleResult ──────────────────────────────────────────────────────────────
// Havale talebi başarılı döndüğünde adminlere gösterilen bilgi kartı
function HavaleResult({ result, onClose, t }) {
  if (!result) return null;
  const bank = result.bank_info || {};
  return (
    <div className="card-clay p-5 mb-4 border-2 border-emerald-300 bg-emerald-50 space-y-3">
      <div className="flex justify-between items-center">
        <h3 className="font-bold text-sm flex items-center gap-2 text-emerald-700">
          <CheckCircle2 size={18} className="text-emerald-500" />
          {t('admin.acc_havale_result_title')}
        </h3>
        <button onClick={onClose} aria-label={t('admin.btn_close')} className="p-1 rounded hover:bg-emerald-100">
          <X size={16} />
        </button>
      </div>

      {/* KO-STD referansı — öne çıkar */}
      <div className="bg-white rounded-xl p-3 border-2 border-emerald-200">
        <p className="text-[10px] font-bold text-[var(--text-muted)] mb-1">{t('admin.acc_havale_result_ref')}</p>
        <div className="flex items-center gap-1">
          <span className="font-mono font-bold text-lg text-emerald-700">{result.payment_ref}</span>
          <CopyButton text={result.payment_ref} t={t} />
        </div>
        <p className="text-[10px] text-[var(--text-muted)] mt-1">{t('admin.acc_havale_result_ref_hint')}</p>
      </div>

      {/* Tutar */}
      <div className="flex items-center justify-between text-sm">
        <span className="font-semibold text-[var(--text-main)]">{t('admin.acc_col_amount')}</span>
        <span className="font-bold text-emerald-700">{fmtTRY(result.amount)}</span>
      </div>

      {/* Banka bilgisi */}
      {(bank.bank_name || bank.iban) && (
        <div className="bg-white rounded-xl p-3 border border-emerald-200 space-y-1">
          <p className="text-[10px] font-bold text-[var(--text-muted)]">{t('admin.acc_havale_result_bank_info')}</p>
          {bank.bank_name && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-[var(--text-muted)]">{t('admin.acc_bank_name')}</span>
              <span className="font-semibold">{bank.bank_name}</span>
            </div>
          )}
          {bank.iban && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-[var(--text-muted)]">IBAN</span>
              <div className="flex items-center">
                <span className="font-mono font-bold">{bank.iban}</span>
                <CopyButton text={bank.iban} t={t} />
              </div>
            </div>
          )}
          {bank.account_no && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-[var(--text-muted)]">{t('admin.acc_account_no')}</span>
              <span className="font-mono">{bank.account_no}</span>
            </div>
          )}
        </div>
      )}

      {/* İnstructions / admin notu */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2 text-xs text-amber-700">
        <Info size={14} className="shrink-0 mt-0.5" />
        <p>{t('admin.acc_havale_result_instruction')}</p>
      </div>

      {result.instructions && (
        <p className="text-xs text-[var(--text-muted)] italic">{result.instructions}</p>
      )}
    </div>
  );
}

// ─── BankTxForm ───────────────────────────────────────────────────────────────
function BankTxForm({ saving, onSave, onClose, t, banks }) {
  const [form, setForm] = useState({
    amount: 0,
    direction: 'in',
    sender_name: '',
    sender_iban: '',
    description: '',
    transaction_date: TODAY,
    bank_id: banks?.length === 1 ? banks[0].bank_id : '',
    bank_name: banks?.length === 1 ? banks[0].name : '',
    ext_ref: '',
  });
  const upd = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const fieldCls = "w-full px-2 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-xs";

  const handleBankSelect = (bankId) => {
    const bank = (banks || []).find(b => b.bank_id === bankId);
    setForm(s => ({ ...s, bank_id: bankId, bank_name: bank ? bank.name : '' }));
  };

  return (
    <div className="card-clay p-4 mb-4 space-y-3">
      <div className="flex justify-between items-center">
        <h3 className="font-bold text-sm">{t('admin.acc_match_form_title')}</h3>
        <button onClick={onClose} aria-label={t('admin.btn_close')} className="p-1 rounded hover:bg-gray-100">
          <X size={16} />
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <div>
          <label htmlFor="btx-amount" className="text-[10px] font-bold block mb-0.5">
            {t('admin.acc_match_form_amount')} <span className="text-red-500">*</span>
          </label>
          <input
            id="btx-amount"
            type="number"
            step="0.01"
            min="0"
            value={form.amount}
            onChange={e => upd('amount', parseFloat(e.target.value) || 0)}
            className={fieldCls}
          />
        </div>
        <div>
          <label htmlFor="btx-direction" className="text-[10px] font-bold block mb-0.5">
            {t('admin.acc_match_form_direction')} <span className="text-red-500">*</span>
          </label>
          <select
            id="btx-direction"
            value={form.direction}
            onChange={e => upd('direction', e.target.value)}
            className={fieldCls}
          >
            <option value="in">{t('admin.acc_match_form_direction_in')}</option>
            <option value="out">{t('admin.acc_match_form_direction_out')}</option>
          </select>
        </div>
        <div>
          <label htmlFor="btx-sender-name" className="text-[10px] font-bold block mb-0.5">
            {t('admin.acc_match_form_sender_name')} <span className="text-red-500">*</span>
          </label>
          <input
            id="btx-sender-name"
            value={form.sender_name}
            onChange={e => upd('sender_name', e.target.value)}
            className={fieldCls}
          />
        </div>
        <div>
          <label htmlFor="btx-sender-iban" className="text-[10px] font-bold block mb-0.5">
            {t('admin.acc_match_form_sender_iban')}
          </label>
          <input
            id="btx-sender-iban"
            value={form.sender_iban}
            onChange={e => upd('sender_iban', e.target.value.toUpperCase())}
            className={`${fieldCls} font-mono`}
            maxLength={32}
          />
        </div>
        <div>
          <label htmlFor="btx-date" className="text-[10px] font-bold block mb-0.5">
            {t('admin.acc_match_form_date')} <span className="text-red-500">*</span>
          </label>
          <input
            id="btx-date"
            type="date"
            value={form.transaction_date}
            onChange={e => upd('transaction_date', e.target.value)}
            className={fieldCls}
          />
        </div>
        <div>
          <label htmlFor="btx-bank" className="text-[10px] font-bold block mb-0.5">
            {t('admin.acc_match_form_bank')} <span className="text-red-500">*</span>
          </label>
          <select
            id="btx-bank"
            value={form.bank_id}
            onChange={e => handleBankSelect(e.target.value)}
            className={fieldCls}
          >
            <option value="">{t('admin.acc_cash_register')}</option>
            {(banks || []).map(b => (
              <option key={b.bank_id} value={b.bank_id}>{b.name}</option>
            ))}
          </select>
        </div>
        <div className="col-span-2">
          <label htmlFor="btx-desc" className="text-[10px] font-bold block mb-0.5">
            {t('admin.acc_match_form_description')}
          </label>
          <input
            id="btx-desc"
            value={form.description}
            onChange={e => upd('description', e.target.value)}
            className={fieldCls}
          />
        </div>
        <div>
          <label htmlFor="btx-ext-ref" className="text-[10px] font-bold block mb-0.5">
            {t('admin.acc_match_form_ext_ref')}
          </label>
          <input
            id="btx-ext-ref"
            value={form.ext_ref}
            onChange={e => upd('ext_ref', e.target.value)}
            className={`${fieldCls} font-mono`}
          />
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => onSave(form)}
          disabled={saving}
          className="btn-success px-4 py-1.5 text-xs disabled:opacity-50 min-h-[36px]"
        >
          {saving ? t('admin.acc_match_form_saving') : t('admin.acc_match_form_save')}
        </button>
        <button onClick={onClose} disabled={saving} className="px-4 py-1.5 text-xs rounded-xl border-2 border-[var(--border-light)] font-bold disabled:opacity-50">
          {t('admin.btn_cancel')}
        </button>
      </div>
    </div>
  );
}

// ─── MatchModal ────────────────────────────────────────────────────────────────
// FIX 1: paymentId state'i suggested_payment_id (pay_xxx) ile besleniyor — backend bunu bekliyor.
// suggested_payment_ref (KO-STD-xxx) yalnızca GÖSTERIM içindir, hiçbir zaman submit edilmez.
function MatchModal({ tx, matching, onMatch, onClose, t }) {
  // Default: öneri varsa suggested_payment_id (pay_xxx); yoksa boş
  const [paymentId, setPaymentId] = useState(tx?.suggested_payment_id || '');
  if (!tx) return null;
  const hasSuggestion = !!(tx.suggested_payment_id);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card-clay p-5 max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-sm flex items-center gap-2">
            <Link2 size={16} className="text-[var(--brand-primary)]" />
            {t('admin.acc_match_modal_title')}
          </h3>
          <button onClick={onClose} aria-label={t('admin.btn_close')} className="p-1 rounded hover:bg-gray-100">
            <X size={16} />
          </button>
        </div>

        {/* Tx summary */}
        <div className="bg-gray-50 rounded-xl p-3 mb-4 text-xs space-y-1">
          <p className="font-semibold">{tx.sender_name}</p>
          <p className="text-[var(--text-muted)]">{fmtDate(tx.transaction_date)} — {tx.bank_name}</p>
          <p className="font-bold text-green-600">{fmtTRY(tx.amount)}</p>
          {tx.description && <p className="text-[var(--text-muted)] truncate">{tx.description}</p>}
        </div>

        {/* Öneri rozeti — KO-STD referansını göster, pay_xxx'i arka planda tut */}
        {hasSuggestion && (
          <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-3 mb-3 text-xs flex items-start gap-2">
            <Info size={14} className="text-amber-500 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-amber-700">{t('admin.acc_match_modal_suggested')}</p>
              {/* KO-STD referansını göster (okunabilir) */}
              <p className="font-mono mt-0.5 text-amber-900">{tx.suggested_payment_ref || tx.suggested_payment_id}</p>
              {tx.suggested_student_name && (
                <p className="text-amber-700 mt-0.5">{tx.suggested_student_name} — {fmtTRY(tx.suggested_amount || 0)}</p>
              )}
              {/* "Bu ödemeyle eşleştir" — arka planda suggested_payment_id kullanır */}
              <button
                onClick={() => setPaymentId(tx.suggested_payment_id)}
                className="mt-2 px-3 py-1 rounded-xl bg-amber-500 text-white text-[10px] font-bold hover:bg-amber-600 flex items-center gap-1"
              >
                <Check size={10} />
                {t('admin.acc_match_modal_use_suggestion')}
              </button>
            </div>
          </div>
        )}

        {/* Manuel giriş — admin pay_xxx yazabilir; öneri varsa zaten dolmuş */}
        <div className="mb-4">
          <label htmlFor="match-payment-id" className="text-xs font-bold block mb-1">
            {t('admin.acc_match_modal_payment_id_manual')}
          </label>
          <input
            id="match-payment-id"
            value={paymentId}
            onChange={e => setPaymentId(e.target.value)}
            placeholder={t('admin.acc_match_modal_placeholder')}
            className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-mono text-xs"
          />
          <p className="text-[10px] text-[var(--text-muted)] mt-0.5">{t('admin.acc_match_modal_id_hint')}</p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => onMatch(tx.transaction_id, paymentId)}
            disabled={matching || !paymentId.trim()}
            className="btn-primary px-6 py-2 text-xs flex-1 disabled:opacity-50 min-h-[44px]"
          >
            {matching ? t('admin.acc_match_modal_confirming') : t('admin.acc_match_modal_confirm')}
          </button>
          <button onClick={onClose} disabled={matching} className="px-4 py-2 text-xs rounded-xl border-2 border-[var(--border-light)] font-bold disabled:opacity-50 min-h-[44px]">
            {t('admin.btn_cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ConfidenceBadge ──────────────────────────────────────────────────────────
// match_confidence (100/90/70/0) + match_method → renkli rozet
function ConfidenceBadge({ confidence, method, t }) {
  if (confidence === undefined || confidence === null) return null;

  let label, cls, ariaLabel;
  if (confidence === 100) {
    label = t('admin.acc_match_confidence_full');
    cls = 'bg-emerald-50 text-emerald-700 border-emerald-200';
    ariaLabel = `${label} (${confidence}%)`;
  } else if (confidence >= 90 || method === 'name_amount') {
    label = t('admin.acc_match_confidence_name_amount');
    cls = 'bg-blue-50 text-blue-700 border-blue-200';
    ariaLabel = `${label} (${confidence}%)`;
  } else if (confidence >= 70 || method === 'amount_only') {
    label = t('admin.acc_match_confidence_amount_only');
    cls = 'bg-amber-50 text-amber-700 border-amber-200';
    ariaLabel = `${label} (${confidence}%)`;
  } else {
    label = t('admin.acc_match_confidence_unknown');
    cls = 'bg-gray-50 text-gray-500 border-gray-200';
    ariaLabel = label;
  }

  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[10px] font-bold ${cls}`}
      aria-label={ariaLabel}
    >
      {confidence > 0 ? `${confidence}%` : ''} {label}
    </span>
  );
}

// ─── BankConnectorPanel ───────────────────────────────────────────────────────
function BankConnectorPanel({ t, authHeaders, toast, lang }) {
  const [connectors, setConnectors] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const fetchConnectors = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await axios.get(`${API}/admin/bank-connectors`, { headers: authHeaders() });
      // Backend {connectors:[...], configured_count, ...} objesi döner; diziyi çıkar.
      setConnectors(res.data?.connectors || (Array.isArray(res.data) ? res.data : []));
    } catch (err) {
      setLoadError(true);
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  }, [authHeaders, toast, lang]);

  useEffect(() => { fetchConnectors(); }, [fetchConnectors]);

  return (
    <div className="card-clay p-4 mt-6">
      <div className="flex items-center gap-2 mb-3">
        <Landmark size={16} className="text-[var(--brand-primary)]" />
        <h4 className="font-bold text-sm">{t('admin.acc_connector_panel_title')}</h4>
      </div>
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 flex items-start gap-2 text-xs text-blue-700">
        <Info size={14} className="shrink-0 mt-0.5" />
        <p>{t('admin.acc_connector_panel_desc')}</p>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-[var(--text-muted)] py-2">
          <RefreshCw size={14} className="animate-spin" />
          {t('admin.acc_match_loading')}
        </div>
      ) : loadError ? (
        <div className="text-xs text-red-500 flex items-center gap-2">
          <AlertTriangle size={14} />
          {t('admin.acc_match_load_error')}
          <button onClick={fetchConnectors} className="underline font-bold">{t('admin.acc_retry')}</button>
        </div>
      ) : !connectors || connectors.length === 0 ? (
        <p className="text-xs text-[var(--text-muted)]">{t('admin.acc_match_no_records')}</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {connectors.map(c => (
            <div
              key={c.bank_key}
              className="flex items-center justify-between px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-xs gap-2"
            >
              <span className="font-semibold truncate">{c.display_name || c.bank_key}</span>
              {c.is_configured ? (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold whitespace-nowrap"
                  aria-label={t('admin.acc_connector_status_connected')}
                >
                  <CheckCircle2 size={10} aria-hidden="true" />
                  {t('admin.acc_connector_status_connected')}
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-gray-50 text-gray-500 border border-gray-200 text-[10px] font-bold whitespace-nowrap"
                  aria-label={t('admin.acc_connector_status_pending')}
                >
                  {t('admin.acc_connector_status_pending')}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── AdminAccounting ──────────────────────────────────────────────────────────
export default function AdminAccounting() {
  const { lang, t } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();

  const [saving, setSaving] = useState({ entry: false, bank: false, note: false, noteCollect: false, collect: false, expense: false, refund: false, banktx: false, matching: false, csvImporting: false, havale: false, autoMatch: false });
  const [data, setData] = useState({
    entries: [], total_income: 0, total_expense: 0, net: 0,
    pending_amount: 0, overdue_amount: 0, bank_total: 0,
    total_refunded: 0, net_income: 0, gross_income: 0,
  });
  const [tab, setTab] = useState('overview');
  const [banks, setBanks] = useState([]);
  // `students` (mount'ta çekilen toplu öğrenci listesi) Tur 3'te kaldırıldı —
  // RefundForm/HavaleRequestForm/"Senet Ekle" artık StudentSearchCombobox
  // kullanıyor, tüketicisi kalmadı (feature/mali-isler-ozet-tarih-web).
  const [expenseSummary, setExpenseSummary] = useState([]);
  // InterestSettingsPanel kendi loadError state'ini kendi içinde tutuyor

  // ── Overview AKIŞ kartları (brüt gelir/iade/net gelir/gider/net kâr) —
  // sunucu taraflı, "Listele" ile ────────────────────────────────────────────
  // (feature/mali-isler-ozet-tarih-web, Tur 2) Eskiden bu 5 kart mount'ta bir
  // kere çekilen `/admin/accounting`'in TÜM-ZAMANLAR toplamlarını gösteriyordu
  // (data.total_income vb., aşağıdaki fetchAll'da hâlâ set ediliyor ama artık
  // KPI kartlarında OKUNMUYOR). Diğer 6 liste sekmesindeki appliedFilters/
  // "Listele" deseniyle aynı fikir; ama /admin/accounting/lists/summary
  // agregasyon dönüyor (items/total sayfalama YOK), o yüzden useAccountingList
  // yerine fetchExpenseSummary ile AYNI basit fetch şekli kullanıldı — arama
  // kutusu da yok (agregat rakam, satır listesi değil). DURUM kartları (banka
  // toplamı/bekleyen taksit/geciken/faizli taksit) bu state'e BAĞLI DEĞİL,
  // fetchAll'daki mount fetch'inden anlık beslenmeye devam ediyor.
  const [overviewDateRange, setOverviewDateRange] = useState({ from: null, to: null });
  const [overviewSummary, setOverviewSummary] = useState(null);
  const [overviewSummaryLoading, setOverviewSummaryLoading] = useState(false);
  const [overviewSummarySearched, setOverviewSummarySearched] = useState(false);
  const [overviewAppliedRange, setOverviewAppliedRange] = useState(null); // { from, to } ISO — kartların üstündeki etiket için

  // ── Mali İşler liste sekmeleri (Hesap Hareketleri / Taksitler / Geciken /
  // Senetler / Giderler / İadeler) — sunucu taraflı, "Listele" ile ────────────
  // Her sekme kendi useAccountingList örneğini + taslak (draft) dateRange/
  // search state'ini taşır. appliedFilters null olduğu sürece (yani kullanıcı
  // henüz "Listele"ye basmadıysa) hiçbir fetch atılmaz — bkz. dosya başı yorum.
  const instList = useAccountingList({ endpoint: 'installments', authHeaders, lang, toast });
  const [instDateRange, setInstDateRange] = useState({ from: null, to: null });

  const overdueList = useAccountingList({ endpoint: 'overdue', authHeaders, lang, toast });
  const [overdueDateRange, setOverdueDateRange] = useState({ from: null, to: null });
  const [overdueSearch, setOverdueSearch] = useState('');

  const notesList = useAccountingList({ endpoint: 'promissory-notes', authHeaders, lang, toast });
  const [notesDateRange, setNotesDateRange] = useState({ from: null, to: null });

  // Ledger (Hesap Hareketleri) — final_balance sunucu yanıtında ayrı bir alan
  // olduğu için useAccountingList'in onSuccess kancasıyla senkronlanıyor.
  const [ledgerFinalBalance, setLedgerFinalBalance] = useState(null);
  const ledgerList = useAccountingList({
    endpoint: 'ledger', authHeaders, lang, toast,
    onSuccess: (resData) => setLedgerFinalBalance(resData?.final_balance ?? null),
  });
  const [ledgerDateRange, setLedgerDateRange] = useState({ from: null, to: null });
  const [ledgerSearch, setLedgerSearch] = useState('');

  const expensesList = useAccountingList({ endpoint: 'expenses', authHeaders, lang, toast });
  const [expensesDateRange, setExpensesDateRange] = useState({ from: null, to: null });

  const refundsList = useAccountingList({ endpoint: 'refunds', authHeaders, lang, toast });
  const [refundsDateRange, setRefundsDateRange] = useState({ from: null, to: null });

  // Bank statement modal state
  const [statementBank, setStatementBank] = useState(null); // { bank_id, name }

  // Match Pool state
  const [bankTxs, setBankTxs] = useState([]);
  const [loadingBankTxs, setLoadingBankTxs] = useState(false);
  const [errorBankTxs, setErrorBankTxs] = useState(false);
  const [showBankTxForm, setShowBankTxForm] = useState(false);
  const [showCsvPanel, setShowCsvPanel] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvResult, setCsvResult] = useState(null);
  const [matchModal, setMatchModal] = useState(null);
  const [bankTxSearch, setBankTxSearch] = useState('');
  const [showHavaleForm, setShowHavaleForm] = useState(false);
  const [havaleResult, setHavaleResult] = useState(null);

  // Entry form
  const [_showEntryForm, setShowEntryForm] = useState(false);
  const [entryForm, setEntryForm] = useState({ type: 'income', category: '', description: '', amount: 0, date: TODAY, bank_id: '' });

  // Bank form
  const [showBankForm, setShowBankForm] = useState(false);
  const [bankForm, setBankForm] = useState({ name: '', account_type: 'bank', iban: '', account_no: '', branch: '', currency: 'TRY', balance: 0 });
  const [editingBank, setEditingBank] = useState(null);

  // Note form
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteForm, setNoteForm] = useState({ user_id: '', amount: 0, due_date: '', note_number: '', description: '', invoice_id: '' });
  const [noteInvoices, setNoteInvoices] = useState([]);
  const [loadingNoteInvoices, setLoadingNoteInvoices] = useState(false);
  // Senet tahsilat modali — bank_id zorunlu (A0-5)
  const [noteCollectTarget, setNoteCollectTarget] = useState(null);

  // Senet formunda öğrenci seçilince faturalarını çek (A0-3 — invoice_id zorunlu)
  useEffect(() => {
    if (!noteForm.user_id) {
      setNoteInvoices([]);
      setNoteForm(f => ({ ...f, invoice_id: '' }));
      return;
    }
    let cancelled = false;
    setLoadingNoteInvoices(true);
    setNoteInvoices([]);
    setNoteForm(f => ({ ...f, invoice_id: '' }));
    axios.get(`${API}/admin/invoices`, {
      headers: authHeaders(),
      params: { user_id: noteForm.user_id, limit: 200 },
    })
      .then(res => {
        if (!cancelled) setNoteInvoices(res.data?.invoices || []);
      })
      .catch(err => {
        if (!cancelled) toast.error(getApiErrorMessage(err, lang));
      })
      .finally(() => {
        if (!cancelled) setLoadingNoteInvoices(false);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteForm.user_id]);

  // Expense form
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  // Refund form
  const [showRefundForm, setShowRefundForm] = useState(false);
  const [refundSearch, setRefundSearch] = useState('');
  const [refundStatusFilter, setRefundStatusFilter] = useState('');
  const [refundMethodFilter, setRefundMethodFilter] = useState('');

  // Collect modal
  const [collectModal, setCollectModal] = useState(null);
  // Moka ödeme linki dialog'u — taksit satırı state'i, CollectModal ile AYNI desen.
  const [mokaLinkModal, setMokaLinkModal] = useState(null);
  const { data: mokaConfig } = useMokaConfig();

  // Confirm dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({ title: '', desc: '', onConfirm: null, variant: 'danger' });
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Filter state
  const [installmentSearch, setInstallmentSearch] = useState('');
  const [noteSearch, setNoteSearch] = useState('');
  const [expenseSearch, setExpenseSearch] = useState('');
  const [expenseCategoryFilter, setExpenseCategoryFilter] = useState('');

  const openConfirm = (title, desc, onConfirm, variant = 'danger') => {
    setConfirmConfig({ title, desc, onConfirm, variant });
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (!confirmConfig.onConfirm) return;
    setConfirmLoading(true);
    try { await confirmConfig.onConfirm(); }
    finally { setConfirmLoading(false); setConfirmOpen(false); }
  };

  // ── Fetch core (Overview KPI — İSTİSNA, DOKUNULMADI) ────────────────────────
  // Yalnızca genel bakış KPI kartları + form dropdown kaynağı (bankalar) için.
  // 6 liste sekmesi (installments/overdue/notes/ledger/expenses/refunds) ARTIK
  // burada YOK — her biri kendi useAccountingList örneğiyle "Listele"
  // tıklanınca ayrı ayrı çekiliyor (aşağıdaki handleListXxx). NOT: `/admin/
  // accounting` yanıtı `overdue_count` alanını ZATEN içeriyor (routes/
  // payments.py:352) — overdueCount KPI kartı buradan besleniyor, ayrı bir
  // çağrıya gerek yok. Öğrenci listesi (`/admin/student-ops`) Tur 3'te
  // (feature/mali-isler-ozet-tarih-web) buradan KALDIRILDI — form dropdown'ları
  // artık StudentSearchCombobox ile arama-tabanlı çekiyor, mount'ta toplu
  // öğrenci listesine gerek kalmadı.
  const fetchAll = async () => {
    try {
      const [accRes, bankRes] = await Promise.all([
        axios.get(`${API}/admin/accounting`, { headers: authHeaders() }),
        axios.get(`${API}/admin/banks`, { headers: authHeaders() }),
      ]);
      setData(prev => ({
        entries: [], total_income: 0, total_expense: 0, net: 0,
        pending_amount: 0, overdue_amount: 0, bank_total: 0,
        total_refunded: 0, net_income: 0, gross_income: 0,
        ...prev,
        ...accRes.data,
      }));
      setBanks(bankRes.data?.banks || []);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  // ── Overview "Geciken" önizlemesi — mount'ta, "Listele" BEKLEMEDEN çalışır
  // (düzeltme turu, proje-yoneticisi: Overview KPI'larının "Listele"ye bağlı
  // kalması regresyon kabul edildi). Overdue TAB'inin kendi `overdueList`
  // (useAccountingList) örneğinden AYRI, küçük bir önizleme: geniş sabit
  // tarih aralığı (2000-01-01 .. bugün) + limit=5. Overdue TAB'in kendisi
  // hâlâ "Listele" bekliyor — bu YALNIZCA Overview'un hafif önizlemesi.
  const [overduePreview, setOverduePreview] = useState({ rows: [], total: 0 });
  const fetchOverduePreview = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/accounting/lists/overdue`, {
        headers: authHeaders(),
        params: { date_from: '2000-01-01', date_to: TODAY, limit: 5 },
      });
      setOverduePreview({ rows: res.data?.items || [], total: res.data?.total || 0 });
    } catch {
      // Sessiz — Overview önizlemesi ikincil bir widget; asıl KPI'lar
      // /admin/accounting'den geliyor, burada toast ile kullanıcıyı boğmuyoruz.
    }
  }, [authHeaders]);

  // ── "Faizli Taksit" KPI'ı — 2. düzeltme turu: interestCount önceden
  // overduePreview.total'a (yani Geciken sayısına) eşitti — reviewer bunu
  // anlamsal regresyon olarak işaretledi (iki farklı KPI aynı sayıyı
  // gösteriyordu). Backend artık gerçek sayıyı veriyor: GET /admin/
  // accounting/lists/interest-count → {interest_count} (parametresiz, tanım
  // eski koddaki `status === 'interest' || (interest_amount > 0 && status !==
  // 'paid')` ile birebir). Mount'ta, sessiz hatayla çekiliyor; Overdue
  // önizlemesinden bağımsız ayrı bir state.
  const [interestCount, setInterestCount] = useState(0);
  const fetchInterestCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/accounting/lists/interest-count`, { headers: authHeaders() });
      setInterestCount(res.data?.interest_count || 0);
    } catch {
      // Sessiz — bkz. fetchOverduePreview üstündeki aynı gerekçe.
    }
  }, [authHeaders]);

  // ── Gider özeti — Giderler sekmesinde "Listele" ile birlikte çekilir ────────
  // (/admin/expenses/summary zaten date_from/date_to destekliyor, bkz.
  // backend/routes/expenses.py — yeni sözleşmeye dahil değil ama aynı tarih
  // aralığıyla ayrıca çağrılıyor.)
  const fetchExpenseSummary = useCallback((dateFrom, dateTo) => {
    axios.get(`${API}/admin/expenses/summary`, { headers: authHeaders(), params: { date_from: dateFrom, date_to: dateTo } })
      .then(res => setExpenseSummary(res.data?.summary || res.data || []))
      .catch(err => toast.error(getApiErrorMessage(err, lang)));
  }, [authHeaders, toast, lang]);

  // ── Overview akış özeti — DateRange + "Listele" ile çekilir ─────────────────
  // GET /admin/accounting/lists/summary — response anahtarları eski
  // /admin/accounting akış kartı anahtarlarıyla AYNI (total_income,
  // total_refunded, net_income, total_expense, net_profit).
  const overviewSummarySeq = useRef(0);
  const fetchOverviewSummary = useCallback((dateFrom, dateTo) => {
    const seq = ++overviewSummarySeq.current;
    setOverviewSummaryLoading(true);
    axios.get(`${API}/admin/accounting/lists/summary`, { headers: authHeaders(), params: { date_from: dateFrom, date_to: dateTo } })
      .then(res => {
        if (seq !== overviewSummarySeq.current) return;
        setOverviewSummary(res.data || {});
      })
      .catch(err => {
        if (seq !== overviewSummarySeq.current) return;
        setOverviewSummary(null);
        toast.error(getApiErrorMessage(err, lang));
      })
      .finally(() => {
        if (seq !== overviewSummarySeq.current) return;
        setOverviewSummaryLoading(false);
      });
  }, [authHeaders, toast, lang]);

  // ── "Listele" handler'ları ───────────────────────────────────────────────────
  // Her biri kendi taslak (draft) dateRange/search'ünden appliedFilters
  // üretir; tarih aralığı seçilmeden çağrılırsa (buton zaten disabled) hiçbir
  // şey yapmaz — savunma amaçlı ikinci kontrol (AdminMuhasebeListesi.jsx'teki
  // handleListele deseniyle aynı).
  const handleListOverview = () => {
    if (!overviewDateRange?.from || !overviewDateRange?.to) return;
    const date_from = toIsoDate(overviewDateRange.from);
    const date_to = toIsoDate(overviewDateRange.to);
    setOverviewSummarySearched(true);
    setOverviewAppliedRange({ from: date_from, to: date_to });
    fetchOverviewSummary(date_from, date_to);
  };
  const handleListInstallments = () => {
    if (!instDateRange?.from || !instDateRange?.to) return;
    instList.applyFilters({
      date_from: toIsoDate(instDateRange.from),
      date_to: toIsoDate(instDateRange.to),
      q: installmentSearch || undefined,
    });
  };
  const handleListOverdue = () => {
    if (!overdueDateRange?.from || !overdueDateRange?.to) return;
    overdueList.applyFilters({
      date_from: toIsoDate(overdueDateRange.from),
      date_to: toIsoDate(overdueDateRange.to),
      q: overdueSearch || undefined,
    });
  };
  const handleListNotes = () => {
    if (!notesDateRange?.from || !notesDateRange?.to) return;
    notesList.applyFilters({
      date_from: toIsoDate(notesDateRange.from),
      date_to: toIsoDate(notesDateRange.to),
      q: noteSearch || undefined,
    });
  };
  const handleListLedger = () => {
    if (!ledgerDateRange?.from || !ledgerDateRange?.to) return;
    ledgerList.applyFilters({
      date_from: toIsoDate(ledgerDateRange.from),
      date_to: toIsoDate(ledgerDateRange.to),
      q: ledgerSearch || undefined,
    });
  };
  const handleListExpenses = () => {
    if (!expensesDateRange?.from || !expensesDateRange?.to) return;
    const date_from = toIsoDate(expensesDateRange.from);
    const date_to = toIsoDate(expensesDateRange.to);
    expensesList.applyFilters({
      date_from, date_to,
      q: expenseSearch || undefined,
      category: expenseCategoryFilter || undefined,
    });
    fetchExpenseSummary(date_from, date_to);
  };
  const handleListRefunds = () => {
    if (!refundsDateRange?.from || !refundsDateRange?.to) return;
    refundsList.applyFilters({
      date_from: toIsoDate(refundsDateRange.from),
      date_to: toIsoDate(refundsDateRange.to),
      q: refundSearch || undefined,
      status: refundStatusFilter || undefined,
      method: refundMethodFilter || undefined,
    });
  };

  // ── Fetch bank transactions ────────────────────────────────────────────────
  const [bankTxsHasMore, setBankTxsHasMore] = useState(false);
  const fetchBankTxs = useCallback(async () => {
    setLoadingBankTxs(true);
    setErrorBankTxs(false);
    try {
      const res = await axios.get(`${API}/admin/bank-transactions/unmatched`, {
        headers: authHeaders(),
        params: { limit: 200 },
      });
      setBankTxs(res.data?.items || []);
      setBankTxsHasMore(!!res.data?.has_more);
    } catch (err) {
      setErrorBankTxs(true);
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoadingBankTxs(false);
    }
  }, [authHeaders, toast, lang]);

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchOverduePreview(); }, [fetchOverduePreview]);
  useEffect(() => { fetchInterestCount(); }, [fetchInterestCount]);
  useEffect(() => { if (tab === 'match_pool') fetchBankTxs(); }, [tab, fetchBankTxs]);

  // ── Entries ────────────────────────────────────────────────────────────────
  const _saveEntry = async () => {
    if (!entryForm.category || !entryForm.description) { toast.warning(t('admin.acc_validation_category')); return; }
    if (parseFloat(entryForm.amount) <= 0) { toast.warning(t('admin.acc_validation_amount')); return; }
    setSaving(s => ({ ...s, entry: true }));
    try {
      await axios.post(`${API}/admin/accounting`, entryForm, { headers: authHeaders() });
      toast.success(t('admin.acc_entry_saved'));
      setShowEntryForm(false);
      setEntryForm({ type: 'income', category: '', description: '', amount: 0, date: TODAY, bank_id: '' });
      fetchAll();
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
    finally { setSaving(s => ({ ...s, entry: false })); }
  };

  // ── Banks ──────────────────────────────────────────────────────────────────
  const openEditBank = (bank) => {
    setEditingBank(bank);
    setBankForm({
      name: bank.name || '',
      account_type: bank.account_type || 'bank',
      iban: bank.iban || '',
      account_no: bank.account_no || '',
      branch: bank.branch || '',
      currency: bank.currency || 'TRY',
      balance: bank.balance || 0,
    });
    setShowBankForm(true);
  };
  const closeBankForm = () => { setShowBankForm(false); setEditingBank(null); setBankForm({ name: '', account_type: 'bank', iban: '', account_no: '', branch: '', currency: 'TRY', balance: 0 }); };
  const saveBank = async () => {
    if (!bankForm.name?.trim()) { toast.warning(t('admin.acc_validation_bank_name')); return; }
    const accountType = bankForm.account_type || 'bank';
    const isCash = accountType === 'cash';
    let cleanIban = '';
    if (isCash) {
      // Kasa: IBAN alanı gizli, zorunlu değil ve gönderilmez.
    } else if (accountType === 'bank') {
      if (!bankForm.iban?.trim()) { toast.warning(t('admin.acc_validation_iban')); return; }
      cleanIban = bankForm.iban.replace(/\s/g, '').toUpperCase();
      if (!/^TR\d{24}$/.test(cleanIban)) { toast.warning(t('admin.acc_validation_iban_format')); return; }
    } else if (bankForm.iban?.trim()) {
      // pos: IBAN opsiyonel, girilmişse doğrulanır.
      cleanIban = bankForm.iban.replace(/\s/g, '').toUpperCase();
      if (!/^TR\d{24}$/.test(cleanIban)) { toast.warning(t('admin.acc_validation_iban_format')); return; }
    }
    setSaving(s => ({ ...s, bank: true }));
    try {
      const payload = {
        name: bankForm.name.trim(),
        account_type: accountType,
        currency: bankForm.currency || 'TRY',
        balance: bankForm.balance,
      };
      if (!isCash) {
        payload.iban = cleanIban;
        payload.account_no = bankForm.account_no || '';
        payload.branch = bankForm.branch || '';
      } else {
        // Kasa: eski IBAN/hesap no/şube DB'de kalmasın diye açıkça boşalt.
        payload.iban = '';
        payload.account_no = '';
        payload.branch = '';
      }
      if (editingBank) { await axios.put(`${API}/admin/banks/${editingBank.bank_id}`, payload, { headers: authHeaders() }); toast.success(t('admin.acc_bank_updated')); }
      else { await axios.post(`${API}/admin/banks`, payload, { headers: authHeaders() }); toast.success(t('admin.acc_bank_saved')); }
      closeBankForm(); fetchAll();
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
    finally { setSaving(s => ({ ...s, bank: false })); }
  };

  // ── Notes ──────────────────────────────────────────────────────────────────
  const saveNote = async () => {
    if (!noteForm.user_id) { toast.warning(t('admin.acc_validation_student')); return; }
    if (parseFloat(noteForm.amount) <= 0) { toast.warning(t('admin.acc_validation_amount')); return; }
    if (!noteForm.due_date) { toast.warning(t('admin.acc_validation_due_date')); return; }
    // A0-3 fix: invoice_id backend'de zorunlu — client-side de engelle
    if (!noteForm.invoice_id) { toast.warning(t('admin.acc_note_validation_invoice')); return; }
    setSaving(s => ({ ...s, note: true }));
    try {
      await axios.post(`${API}/admin/promissory-notes`, noteForm, { headers: authHeaders() });
      toast.success(t('admin.acc_add_note'));
      setShowNoteForm(false); setNoteForm({ user_id: '', amount: 0, due_date: '', note_number: '', description: '', invoice_id: '' });
      fetchAll(); notesList.refetchIfApplied();
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
    finally { setSaving(s => ({ ...s, note: false })); }
  };
  const deleteNote = (id) => openConfirm(t('admin.acc_delete_note_title'), t('admin.acc_delete_note_desc'), async () => {
    try {
      await axios.delete(`${API}/admin/promissory-notes/${id}`, { headers: authHeaders() }); toast.success(t('admin.acc_note_deleted')); fetchAll(); notesList.refetchIfApplied();
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
  });
  // A0-5 fix: tahsilatta bank_id zorunlu — inline confirm yerine banka seçimli modal
  const collectNote = (row) => setNoteCollectTarget(row);
  const doCollectNote = async (noteId, form) => {
    if (!form.bank_id) { toast.warning(t('admin.acc_note_collect_bank_required')); return; }
    // POS hesabına tahsilatta aktarılan tutar zorunlu — modal ikisini de
    // kapıda tutuyor, burası son savunma hattı (bkz. lib/posNetAmount.js).
    const netAmountErrorKey = validateNetAmount({
      amount: noteCollectTarget?.amount || 0,
      netAmountRaw: form.net_amount,
      isPos: isSelectedBankPos(banks, form.bank_id),
    });
    if (netAmountErrorKey) {
      toast.warning(t(netAmountErrorI18nKey(netAmountErrorKey)));
      return;
    }
    const netAmountValue =
      form.net_amount === '' || form.net_amount === undefined || form.net_amount === null
        ? null
        : Number(form.net_amount);
    setSaving(s => ({ ...s, noteCollect: true }));
    try {
      await axios.put(
        `${API}/admin/promissory-notes/${noteId}`,
        { status: 'collected', paid_date: TODAY, bank_id: form.bank_id, net_amount: netAmountValue },
        { headers: authHeaders() }
      );
      toast.success(t('admin.acc_note_collected'));
      setNoteCollectTarget(null);
      fetchAll(); notesList.refetchIfApplied();
    } catch (err) { toast.error(noteErrorMessage(err, lang, t)); }
    finally { setSaving(s => ({ ...s, noteCollect: false })); }
  };

  // ── Installments ───────────────────────────────────────────────────────────
  // Backend (FAZ 4 PAKET I) `bank_id`'yi ZORUNLU tutuyor ve tutari taksidin
  // tutariyla karsilastiriyor; modal ikisini de kapida tutuyor, burasi son
  // savunma hatti. Yanit artik taksit belgesine EK olarak `note_sync` tasiyor
  // (senet tarafinda ne olduğunun ozeti) — "sessizce gecme" yasagi geregi
  // kullaniciya bildiriliyor.
  const doCollectInstallment = async (id, form) => {
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.warning(t('admin.acc_validation_amount')); return; }
    if (!form.bank_id) { toast.warning(t('admin.acc_note_collect_bank_required')); return; }
    // POS hesabına tahsilatta aktarılan tutar zorunlu — modal ikisini de
    // kapıda tutuyor, burası son savunma hattı (bkz. lib/posNetAmount.js).
    const netAmountErrorKey = validateNetAmount({
      amount: form.amount,
      netAmountRaw: form.net_amount,
      isPos: isSelectedBankPos(banks, form.bank_id),
    });
    if (netAmountErrorKey) {
      toast.warning(t(netAmountErrorI18nKey(netAmountErrorKey)));
      return;
    }
    // B5 fix — "Hesaba Geçen Tutar" (net_amount) normalize: boş string ->
    // null (komisyon bilgisi yok, backend brüte düşer — CollectionForm.jsx ile
    // AYNI kural). Boş string'i OLDUĞU GİBİ göndermek backend'de
    // services/payment_net.py::compute_net_and_fee içinde float("") ->
    // ValueError -> 400 üretirdi.
    const netAmountValue =
      form.net_amount === '' || form.net_amount === undefined || form.net_amount === null
        ? null
        : Number(form.net_amount);
    setSaving(s => ({ ...s, collect: true }));
    try {
      const body = {
        amount: form.amount,
        payment_method: form.payment_method,
        bank_id: form.bank_id,
        net_amount: netAmountValue,
      };
      const res = await axios.post(`${API}/admin/installments/${id}/collect`, body, { headers: authHeaders() });
      toast.success(t('admin.acc_collected'));
      const sync = res?.data?.note_sync;
      const closedNotes = sync?.collected || [];
      const skippedNotes = sync?.skipped || [];
      if (closedNotes.length > 0) {
        toast.info(t('admin.acc_collect_note_synced').replace('{n}', String(closedNotes.length)));
      }
      if (skippedNotes.length > 0) {
        toast.warning(t('admin.acc_collect_note_skipped').replace('{n}', String(skippedNotes.length)));
      }
      setCollectModal(null);
      fetchAll(); fetchOverduePreview(); fetchInterestCount(); instList.refetchIfApplied(); overdueList.refetchIfApplied();
    } catch (err) { toast.error(noteErrorMessage(err, lang, t)); }
    finally { setSaving(s => ({ ...s, collect: false })); }
  };

  // ── Expenses ───────────────────────────────────────────────────────────────
  const saveExpense = async (form) => {
    // #14 FIX: description boşken acc_validation_category değil, doğru key kullan
    if (!form.description?.trim()) { toast.warning(t('admin.acc_validation_description')); return; }
    if (parseFloat(form.amount) <= 0) { toast.warning(t('admin.acc_validation_amount')); return; }
    setSaving(s => ({ ...s, expense: true }));
    try {
      if (editingExpense) {
        await axios.put(`${API}/admin/expenses/${editingExpense.expense_id}`, form, { headers: authHeaders() });
        toast.success(t('admin.acc_expense_updated'));
      } else {
        await axios.post(`${API}/admin/expenses`, form, { headers: authHeaders() });
        toast.success(t('admin.acc_expense_saved'));
      }
      setShowExpenseForm(false); setEditingExpense(null);
      fetchAll();
      expensesList.refetchIfApplied();
      if (expensesList.appliedFilters) fetchExpenseSummary(expensesList.appliedFilters.date_from, expensesList.appliedFilters.date_to);
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
    finally { setSaving(s => ({ ...s, expense: false })); }
  };

  const deleteExpense = (id) => openConfirm(t('admin.acc_expense_delete_title'), t('admin.acc_expense_delete_desc'), async () => {
    await axios.delete(`${API}/admin/expenses/${id}`, { headers: authHeaders() });
    toast.success(t('admin.acc_expense_deleted'));
    fetchAll();
    expensesList.refetchIfApplied();
    if (expensesList.appliedFilters) fetchExpenseSummary(expensesList.appliedFilters.date_from, expensesList.appliedFilters.date_to);
  });

  const openEditExpense = (exp) => {
    setEditingExpense(exp);
    setShowExpenseForm(true);
  };

  // ── Refunds ────────────────────────────────────────────────────────────────
  const saveRefund = async (form, receiptFile) => {
    // BUG #1 FIX: user_id zorunlu validasyon
    if (!form.user_id) { toast.warning(t('admin.acc_validation_student')); return; }
    // BUG: invoice_id zorunlu (backend Form(...) ile zorunlu)
    if (!form.invoice_id?.trim()) { toast.warning(t('admin.acc_refund_validation_invoice')); return; }
    if (parseFloat(form.amount) <= 0) { toast.warning(t('admin.acc_refund_validation_amount')); return; }
    if (!form.reason) { toast.warning(t('admin.acc_refund_validation_reason')); return; }
    if (!form.method) { toast.warning(t('admin.acc_refund_validation_method')); return; }
    // BUG #4 FIX: Dekont yalnızca havale/EFT için zorunlu; nakit seçiminde opsiyonel
    const needsReceipt = form.method === 'transfer';
    if (needsReceipt && !receiptFile) { toast.warning(t('admin.acc_refund_receipt_required')); return; }

    setSaving(s => ({ ...s, refund: true }));
    try {
      const fd = new FormData();
      // BUG #1 FIX: user_id artık FormData'ya ekleniyor
      fd.append('user_id', form.user_id);
      fd.append('invoice_id', form.invoice_id);
      fd.append('amount', form.amount);
      fd.append('reason', form.reason);
      fd.append('method', form.method);
      if (form.payment_id) fd.append('payment_id', form.payment_id);
      if (form.bank_id) fd.append('bank_id', form.bank_id);
      if (receiptFile) fd.append('receipt', receiptFile);
      // BUG #2 FIX: Content-Type header KALDIRILDI — Axios boundary'yi otomatik ekler
      await axios.post(`${API}/admin/refunds`, fd, { headers: authHeaders() });
      toast.success(t('admin.acc_refund_saved'));
      setShowRefundForm(false);
      fetchAll(); refundsList.refetchIfApplied();
    } catch (err) {
      // Backend yapısal hata objesi ({error_code, message_tr, message_en}) döndürür;
      // getApiErrorMessage doğru dildeki string'i çıkarır (ham obje React'i çökertir).
      toast.error(getApiErrorMessage(err, lang));
    } finally { setSaving(s => ({ ...s, refund: false })); }
  };

  // ── Bank Transactions (Match Pool) ────────────────────────────────────────
  const saveBankTx = async (form) => {
    if (parseFloat(form.amount) <= 0) { toast.warning(t('admin.acc_match_val_amount')); return; }
    if (!form.sender_name?.trim()) { toast.warning(t('admin.acc_match_val_sender')); return; }
    if (!form.transaction_date) { toast.warning(t('admin.acc_match_val_date')); return; }
    if (!form.bank_id && !form.bank_name?.trim()) { toast.warning(t('admin.acc_match_val_bank')); return; }
    setSaving(s => ({ ...s, banktx: true }));
    try {
      const payload = { ...form };
      if (!payload.ext_ref) delete payload.ext_ref;
      await axios.post(`${API}/admin/bank-transactions`, payload, { headers: authHeaders() });
      toast.success(t('admin.acc_match_saved'));
      setShowBankTxForm(false);
      fetchBankTxs();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSaving(s => ({ ...s, banktx: false }));
    }
  };

  const matchTx = async (txId, paymentId) => {
    if (!paymentId?.trim()) { toast.warning(t('admin.acc_match_val_payment')); return; }
    setSaving(s => ({ ...s, matching: true }));
    try {
      const res = await axios.post(
        `${API}/admin/bank-transactions/${txId}/match`,
        { payment_id: paymentId },
        { headers: authHeaders() }
      );
      setMatchModal(null);
      if (res.data?.amount_mismatch && res.data?.mismatch_detail) {
        const d = res.data.mismatch_detail;
        const msg = t('admin.acc_match_mismatch_warn')
          .replace('{{bank}}', fmtTRY(d.bank_amount))
          .replace('{{payment}}', fmtTRY(d.payment_amount))
          .replace('{{diff}}', fmtTRY(Math.abs(d.difference)));
        toast.warning(msg);
      } else {
        toast.success(t('admin.acc_match_success'));
      }
      fetchBankTxs();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSaving(s => ({ ...s, matching: false }));
    }
  };

  const deleteBankTx = (id) => openConfirm(
    t('admin.acc_match_delete_title'),
    t('admin.acc_match_delete_desc'),
    async () => {
      try {
        await axios.delete(`${API}/admin/bank-transactions/${id}`, { headers: authHeaders() });
        toast.success(t('admin.acc_match_deleted'));
        fetchBankTxs();
      } catch (err) {
        const status = err?.response?.status;
        if (status === 409) {
          toast.error(t('admin.acc_match_delete_409'));
        } else {
          toast.error(getApiErrorMessage(err, lang));
        }
      }
    }
  );

  const importCsv = async () => {
    if (!csvFile) return;
    setSaving(s => ({ ...s, csvImporting: true }));
    try {
      const fd = new FormData();
      fd.append('file', csvFile);
      // Content-Type'ı ELLE SET ETME — boundary bozulur
      const res = await axios.post(`${API}/admin/bank-transactions/csv-import`, fd, { headers: authHeaders() });
      const d = res.data;
      const msg = t('admin.acc_match_csv_result')
        .replace('{{imported}}', d.imported ?? 0)
        .replace('{{skipped}}', d.skipped_dedup ?? 0)
        .replace('{{errors}}', d.errors ?? 0);
      setCsvResult({ msg, error_details: d.error_details || [] });
      setCsvFile(null);
      fetchBankTxs();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSaving(s => ({ ...s, csvImporting: false }));
    }
  };

  // ── Havale Talebi ─────────────────────────────────────────────────────────
  const saveHavale = async (payload) => {
    setSaving(s => ({ ...s, havale: true }));
    setHavaleResult(null);
    try {
      const res = await axios.post(`${API}/admin/payment/havale`, payload, { headers: authHeaders() });
      setHavaleResult(res.data);
      setShowHavaleForm(false);
      toast.success(t('admin.acc_havale_created'));
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSaving(s => ({ ...s, havale: false }));
    }
  };

  // ── Auto Match ────────────────────────────────────────────────────────────
  const runAutoMatch = async () => {
    setSaving(s => ({ ...s, autoMatch: true }));
    try {
      const res = await axios.post(`${API}/admin/bank-transactions/auto-match`, {}, { headers: authHeaders() });
      const d = res.data || {};
      const approved = d.auto_approved ?? d.approved ?? 0;
      const suggested = d.suggested ?? 0;
      const processed = d.processed ?? (approved + suggested);
      const msg = t('admin.acc_auto_match_result')
        .replace('{{approved}}', approved)
        .replace('{{suggested}}', suggested)
        .replace('{{processed}}', processed);
      toast.success(msg);
      fetchBankTxs();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSaving(s => ({ ...s, autoMatch: false }));
    }
  };

  // ── KPI data ───────────────────────────────────────────────────────────────
  // AKIŞ kartları (brüt gelir/iade/net gelir/gider/net kâr) — Tur 2
  // (feature/mali-isler-ozet-tarih-web): artık `data` (mount'taki TÜM-ZAMANLAR
  // /admin/accounting cevabı) DEĞİL, Overview'daki DateRange + "Listele" ile
  // çekilen `overviewSummary`'den (GET /admin/accounting/lists/summary)
  // besleniyor. Anahtar adları eskiyle AYNI (total_income, total_refunded,
  // net_income, total_expense, net_profit) — bkz. fetchOverviewSummary yorumu.
  // Henüz "Listele" hiç tıklanmadıysa (overviewSummarySearched === false)
  // rakam yerine kartta ipucu gösterilir (aşağıdaki kpis.map render'ı,
  // kpi.flow === true VE overviewSummarySearched === false ise).
  const overviewFlowReady = overviewSummarySearched && overviewSummary !== null;
  const grossIncome = overviewFlowReady && overviewSummary.total_income !== undefined ? overviewSummary.total_income : 0;
  const refundedAmt = overviewFlowReady && overviewSummary.total_refunded !== undefined ? overviewSummary.total_refunded : 0;
  const netIncome = overviewFlowReady && overviewSummary.net_income !== undefined ? overviewSummary.net_income : (grossIncome - refundedAmt);
  const totalExpenseAmt = overviewFlowReady && overviewSummary.total_expense !== undefined ? overviewSummary.total_expense : 0;
  const netProfit = overviewFlowReady && overviewSummary.net_profit !== undefined ? overviewSummary.net_profit : (netIncome - totalExpenseAmt);
  // DURUM kartları (banka toplamı/bekleyen taksit/geciken/faizli taksit) —
  // DEĞİŞMEDİ: mount'ta çekilen /admin/accounting + interest-count'tan anlık
  // besleniyor, "Listele"ye bağlı DEĞİL.
  // overdueCount/interestCount "Listele"ye BAĞLI DEĞİL: overdueCount `/admin/
  // accounting`'in kendi `overdue_count` alanından, interestCount ise mount'ta
  // çekilen `interestCount` state'inden (GET /admin/accounting/lists/
  // interest-count) besleniyor — bkz. fetchInterestCount üstündeki yorum.
  const overdueCount = data.overdue_count ?? overduePreview.total;

  // `flow: true` olan kartlar overviewFlowReady === false iken rakam yerine
  // admin.acc_list_date_required ipucunu gösterir (kpis.map render'ı).
  const kpis = [
    { label: t('admin.acc_kpi_gross_income'), value: fmtTRY(grossIncome), icon: TrendingUp, color: '#10B981', bg: '#D7FFB8', targetTab: null, flow: true },
    { label: t('admin.acc_kpi_refunded'), value: fmtTRY(refundedAmt), icon: RotateCcw, color: '#8B5CF6', bg: '#EDE9FE', targetTab: 'refunds', flow: true },
    { label: t('admin.acc_kpi_net_income'), value: fmtTRY(netIncome), icon: DollarSign, color: '#0EA5E9', bg: '#E8F5FD', targetTab: null, flow: true },
    { label: t('admin.acc_kpi_expense'), value: fmtTRY(totalExpenseAmt), icon: TrendingDown, color: '#FF4B4B', bg: '#FFE0E0', targetTab: 'expenses', flow: true },
    // FAZ 1 hizalama — "Net Kâr" için negatifte etiket de değişir ("Net Zarar").
    // Kâr/zarar zaten standart muhasebe zıtlığı (aksine banka/defter bakiyesi
    // için tek kelimelik doğal bir zıt yok, o yüzden orada etiket SABİT kalıp
    // ayrı bir rozet/ipucu eklendi — bkz. balanceToneClass yorumu).
    { label: netProfit >= 0 ? t('admin.acc_kpi_net_profit') : t('admin.acc_kpi_net_loss'), value: fmtTRY(Math.abs(netProfit)), icon: DollarSign, color: netProfit >= 0 ? '#10B981' : '#FF4B4B', bg: netProfit >= 0 ? '#D7FFB8' : '#FFE0E0', targetTab: null, flow: true },
    { label: t('admin.acc_kpi_bank_total'), value: fmtTRY(data.bank_total || 0), icon: Landmark, color: '#0EA5E9', bg: '#E8F5FD', targetTab: 'banks' },
    { label: t('admin.acc_kpi_pending'), value: fmtTRY(data.pending_amount || 0), icon: CreditCard, color: '#E5B400', bg: '#FFF4CC', targetTab: 'installments' },
    { label: t('admin.acc_kpi_overdue_count'), value: String(overdueCount), icon: AlertTriangle, color: '#FF4B4B', bg: '#FFE0E0', noTRY: true, targetTab: 'overdue' },
    { label: t('admin.acc_kpi_interest_count'), value: String(interestCount), icon: Info, color: '#F59E0B', bg: '#FEF3C7', noTRY: true, targetTab: 'installments' },
  ];

  // ── Tabs ───────────────────────────────────────────────────────────────────
  const tabConfig = [
    { id: 'overview', label: t('admin.acc_tab_overview') },
    { id: 'entries', label: t('admin.acc_tab_entries') },
    { id: 'banks', label: t('admin.acc_tab_banks') },
    { id: 'installments', label: t('admin.acc_tab_installments') },
    { id: 'overdue', label: `${t('admin.acc_overdue_label')} (${overduePreview.total})` },
    { id: 'notes', label: t('admin.acc_tab_notes') },
    { id: 'expenses', label: t('admin.acc_tab_expenses') },
    { id: 'refunds', label: t('admin.acc_tab_refunds') },
    { id: 'interest', label: t('admin.acc_tab_interest') },
    { id: 'match_pool', label: t('admin.acc_tab_match_pool') },
  ];

  // ── Column definitions ─────────────────────────────────────────────────────
  const _entryCols = [
    { key: 'date', label: t('admin.pay_col_date'), sortable: true, render: v => <span className="text-xs text-[var(--text-muted)]">{v}</span> },
    { key: 'type', label: t('admin.acc_col_type'), render: v => <StatusBadge variant={v === 'income' ? 'success' : 'danger'} label={v === 'income' ? t('admin.acc_income') : t('admin.acc_expense')} /> },
    { key: 'category', label: t('admin.acc_col_category'), render: v => <span className="font-semibold text-xs">{v}</span> },
    { key: 'description', label: t('admin.acc_col_desc'), render: v => <span className="text-xs text-[var(--text-muted)]">{v}</span> },
    { key: 'amount', label: t('admin.acc_col_amount'), sortable: true, render: (v, row) => <span className={`font-bold text-xs ${row.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>{row.type === 'income' ? '+' : '-'}{v} TRY</span> },
  ];

  function installmentStatusLabel(row) {
    const s = row.status;
    if (s === 'paid') return t('admin.acc_status_paid');
    // B2 fix — daha önce 'cancelled' hiç eşleşmiyordu ve varsayılan olarak
    // "Bekliyor" gösteriliyordu (sayfanın üst KPI'ı ile çelişen görüntü).
    // Öğrenci hub'ındaki (accounting.status.cancelled) etiketle AYNI metin.
    if (s === 'cancelled') return t('accounting.status.cancelled');
    if (s === 'legal') return t('admin.acc_status_legal');
    if (s === 'interest') return t('admin.acc_status_interest');
    if (s === 'overdue_warn') return t('admin.acc_status_overdue_warn');
    if (s === 'overdue' || row.is_overdue) return t('admin.acc_status_overdue');
    return t('admin.acc_status_pending');
  }

  const installmentCols = [
    { key: 'user_name', label: t('admin.acc_col_user'), sortable: true, render: v => <span className="font-semibold text-xs">{v}</span> },
    { key: 'installment_no', label: t('admin.acc_col_installment_no'), render: (v, row) => <span className="text-xs text-center">{v}/{row.total_installments}</span> },
    { key: 'amount', label: t('admin.acc_col_amount'), sortable: true, render: v => <span className="font-bold text-xs">{fmtTRY(v)}</span> },
    { key: 'interest_amount', label: t('admin.acc_col_interest'), render: (v) => v > 0 ? <span className="text-xs font-bold text-amber-600">{fmtTRY(v)}</span> : <span className="text-xs text-[var(--text-muted)]">—</span> },
    { key: 'amount_with_interest', label: t('admin.acc_col_total_due'), render: (v, row) => row.interest_amount > 0 ? <span className="text-xs font-bold text-red-600">{fmtTRY(v || row.amount)}</span> : <span className="text-xs text-[var(--text-muted)]">—</span> },
    { key: 'due_date', label: t('admin.acc_col_due'), sortable: true, render: v => <span className="text-xs">{fmtDate(v)}</span> },
    { key: 'days_overdue', label: t('admin.acc_col_days_overdue'), render: v => v > 0 ? <span className="text-xs text-red-500 font-bold">{v}</span> : <span className="text-xs text-[var(--text-muted)]">—</span> },
    { key: 'status', label: t('admin.acc_col_status'), render: (v, row) => <StatusBadge variant={installmentStatusVariant(row.status || (row.is_overdue ? 'overdue' : 'pending'))} label={installmentStatusLabel(row)} /> },
    {
      key: 'actions',
      label: '',
      // B2 fix — önceden yalnızca 'paid' hariç tutuluyordu; 'cancelled' taksit
      // de "Tahsil Et" görüyordu (KPI'ın hariç tuttuğu bir borcu tahsil
      // etmeyi mümkün kılan asıl kapı buydu). Backend ayrıca reddedecek
      // (bkz. routes/payments.py::collect_installment statü guard'ı — başka
      // agent), burası UI'ın kendi kendini yalanlamasını önlüyor.
      render: (_v, row) => {
        if (!INSTALLMENT_TERMINAL_STATUSES.includes(row.status)) {
          return (
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCollectModal(row)}
                className="px-3 py-2 rounded bg-green-50 text-green-600 text-xs font-bold hover:bg-green-100 min-h-[44px]"
                data-testid={`collect-installment-btn-${row.installment_id}`}
              >
                {t('admin.acc_collect')}
              </button>
              {/* Moka ödeme linki — bayrak kapalıyken pasif + açıklama (görev
                  talebi: "link_active false iken pasif + açıklama"). */}
              {mokaConfig && !mokaConfig.link_active ? (
                <span
                  className="px-3 py-2 rounded bg-gray-50 text-[var(--text-muted)] text-xs font-bold inline-flex items-center gap-1 cursor-not-allowed select-none"
                  title={t('admin.moka_row_button_disabled_hint')}
                  aria-label={t('admin.moka_row_button_disabled_hint')}
                  data-testid={`moka-link-btn-disabled-${row.installment_id}`}
                >
                  <Link2 size={13} />
                  {t('admin.moka_row_button')}
                </span>
              ) : (
                <button
                  onClick={() => setMokaLinkModal(row)}
                  className="px-3 py-2 rounded bg-sky-50 text-sky-700 text-xs font-bold hover:bg-sky-100 min-h-[44px] inline-flex items-center gap-1"
                  data-testid={`moka-link-btn-${row.installment_id}`}
                >
                  <Link2 size={13} />
                  {t('admin.moka_row_button')}
                </button>
              )}
            </div>
          );
        }
        if (row.status === 'cancelled') {
          // Kullanıcı NEDEN kapalı olduğunu görsün — native title tooltip,
          // mevcut sözlük kalıbına uygun yeni i18n key (TR/EN dolu).
          return (
            <span
              className="px-3 py-2 rounded bg-gray-50 text-[var(--text-muted)] text-xs font-bold inline-block cursor-not-allowed select-none"
              title={t('admin.acc_collect_disabled_cancelled_hint')}
              aria-label={t('admin.acc_collect_disabled_cancelled_hint')}
              data-testid={`collect-installment-disabled-${row.installment_id}`}
            >
              {t('admin.acc_collect')}
            </span>
          );
        }
        return null; // status === 'paid' — zaten tahsil edilmiş, mevcut davranış korunuyor
      },
    },
  ];

  // Geciken sekmesi (feature/mali-isler-liste-web) — DataTable'a geçirildi,
  // installmentCols'a benzer ama aksiyon her zaman "Tahsil Et" (zaten hepsi açık).
  const overdueCols = [
    {
      key: 'user_name',
      label: t('admin.acc_col_user'),
      render: (v, row) => (
        <div>
          <p className="font-semibold text-xs">{v}</p>
          {row.user_email && <p className="text-[10px] text-[var(--text-muted)]">{row.user_email}</p>}
        </div>
      ),
    },
    { key: 'installment_no', label: t('admin.acc_col_installment_no'), render: (v, row) => <span className="text-xs text-center">{v}/{row.total_installments}</span> },
    { key: 'due_date', label: t('admin.acc_col_due'), sortable: true, render: v => <span className="text-xs text-red-500 font-bold">{fmtDate(v)}</span> },
    { key: 'interest_amount', label: t('admin.acc_col_interest'), render: (v) => v > 0 ? <span className="text-xs font-bold text-amber-600">{fmtTRY(v)}</span> : <span className="text-xs text-[var(--text-muted)]">—</span> },
    { key: 'amount_with_interest', label: t('admin.acc_col_total_due'), sortable: true, render: (v, row) => <span className="font-bold text-xs text-red-500">{fmtTRY(v || row.amount)}</span> },
    { key: 'days_overdue', label: t('admin.acc_col_days_overdue'), render: v => v > 0 ? <span className="text-xs text-red-500 font-bold">{v}</span> : <span className="text-xs text-[var(--text-muted)]">—</span> },
    {
      key: 'actions',
      label: '',
      render: (_v, row) => (
        <button onClick={() => { setCollectModal(row); setTab('installments'); }} className="btn-success px-3 py-2 text-xs">
          {t('admin.acc_collect')}
        </button>
      ),
    },
  ];

  const noteCols = [
    { key: 'note_number', label: t('admin.acc_col_note_no'), render: (v) => v
      ? <span className="font-mono text-xs">{v}</span>
      : <span className="text-xs italic text-[var(--text-muted)]">{t('accounting.promissory_notes.no_number')}</span> },
    { key: 'user_name', label: t('admin.acc_col_user'), sortable: true, render: v => <span className="font-semibold text-xs">{v}</span> },
    { key: 'amount', label: t('admin.acc_col_amount'), sortable: true, render: v => <span className="font-bold text-xs">{fmtTRY(v)}</span> },
    { key: 'due_date', label: t('admin.acc_col_due'), sortable: true, render: v => <span className="text-xs">{fmtDate(v)}</span> },
    { key: 'status', label: t('admin.acc_col_status'), render: (v) => {
      const { variant, label, icon } = promissoryStatusBadgeProps(v, t);
      return <StatusBadge variant={variant} label={label} icon={icon} dot />;
    } },
    { key: 'actions', label: '', render: (_v, row) => row.status === 'active' ? <div className="flex gap-1"><button onClick={() => collectNote(row)} className="px-2 py-1 rounded-xl text-xs font-bold bg-green-50 text-green-600 border border-green-200">{t('admin.acc_collect')}</button><button onClick={() => deleteNote(row.note_id)} className="p-1.5 rounded bg-red-50 text-red-500"><Trash2 size={14} /></button></div> : null },
  ];

  const EXPENSE_CATEGORIES_MAP = {
    rent: t('admin.acc_cat_rent'), bank_fee: t('admin.acc_cat_bank_fee'), salary: t('admin.acc_cat_salary'),
    utility: t('admin.acc_cat_utility'), marketing: t('admin.acc_cat_marketing'), tax: t('admin.acc_cat_tax'),
    office: t('admin.acc_cat_office'), other: t('admin.acc_cat_other'),
  };

  const expenseCols = [
    { key: 'date', label: t('admin.acc_col_date'), sortable: true, render: v => <span className="text-xs text-[var(--text-muted)]">{fmtDate(v)}</span> },
    { key: 'category', label: t('admin.acc_expense_category'), render: v => <span className="font-semibold text-xs">{EXPENSE_CATEGORIES_MAP[v] || v}</span> },
    { key: 'description', label: t('admin.acc_col_desc'), render: v => <span className="text-xs text-[var(--text-muted)]">{v}</span> },
    { key: 'vendor', label: t('admin.acc_expense_vendor'), render: v => v ? <span className="text-xs">{v}</span> : <span className="text-xs text-[var(--text-muted)]">—</span> },
    { key: 'amount', label: t('admin.acc_col_amount'), sortable: true, render: v => <span className="font-bold text-xs text-red-600">-{fmtTRY(v)}</span> },
    { key: 'is_recurring', label: t('admin.acc_expense_recurring'), render: v => v ? <StatusBadge variant="warning" label="R" aria-label={t('admin.acc_expense_recurring')} /> : null },
    { key: 'actions', label: '', render: (_v, row) => (
      <div className="flex gap-1">
        <button onClick={() => openEditExpense(row)} className="p-1.5 rounded bg-blue-50 text-blue-600"><Pencil size={14} /></button>
        <button onClick={() => deleteExpense(row.expense_id)} className="p-1.5 rounded bg-red-50 text-red-500"><Trash2 size={14} /></button>
      </div>
    )},
  ];

  const refundReasonLabel = (r) => {
    const map = { student_request: t('admin.acc_reason_student_request'), course_cancel: t('admin.acc_reason_course_cancel'), content_change: t('admin.acc_reason_content_change'), other: t('admin.acc_reason_other') };
    return map[r] || r;
  };
  const refundMethodLabel = (m) => {
    const map = { transfer: t('admin.acc_method_transfer'), cash: t('admin.acc_method_cash'), moka_refund: t('admin.acc_method_moka_refund') };
    return map[m] || m;
  };
  const refundStatusLabel = (s) => {
    const map = { requested: t('admin.acc_refund_status_requested'), approved: t('admin.acc_refund_status_approved'), completed: t('admin.acc_refund_status_completed'), rejected: t('admin.acc_refund_status_rejected') };
    return map[s] || s;
  };

  const refundCols = [
    { key: 'requested_at', label: t('admin.acc_refund_col_date'), sortable: true, render: v => <span className="text-xs text-[var(--text-muted)]">{fmtDate(v)}</span> },
    { key: 'user_name', label: t('admin.acc_refund_col_user'), render: v => <span className="font-semibold text-xs">{v || '—'}</span> },
    { key: 'amount', label: t('admin.acc_refund_col_amount'), sortable: true, render: v => <span className="font-bold text-xs text-purple-600">{fmtTRY(v)}</span> },
    { key: 'reason', label: t('admin.acc_refund_col_reason'), render: v => <span className="text-xs">{refundReasonLabel(v)}</span> },
    { key: 'method', label: t('admin.acc_refund_col_method'), render: v => <span className="text-xs">{refundMethodLabel(v)}</span> },
    { key: 'status', label: t('admin.acc_refund_col_status'), render: v => <StatusBadge variant={refundStatusVariant(v)} label={refundStatusLabel(v)} dot /> },
  ];

  // ── Match Pool columns ─────────────────────────────────────────────────────
  const bankTxCols = [
    {
      key: 'transaction_date',
      label: t('admin.acc_match_col_date'),
      sortable: true,
      render: v => <span className="text-xs text-[var(--text-muted)]">{fmtDate(v)}</span>,
    },
    {
      key: 'sender_name',
      label: t('admin.acc_match_col_sender'),
      render: (v, row) => (
        <div>
          <p className="font-semibold text-xs">{v}</p>
          {row.sender_iban && <p className="font-mono text-[10px] text-[var(--text-muted)]">{row.sender_iban}</p>}
        </div>
      ),
    },
    {
      key: 'amount',
      label: t('admin.acc_match_col_amount'),
      sortable: true,
      render: (v, row) => {
        const isIn = row.direction === 'in';
        return (
          <div className="flex items-center gap-1">
            {isIn
              ? <ArrowDownLeft size={14} className="text-green-500" aria-label={t('admin.acc_match_dir_in')} />
              : <ArrowUpRight size={14} className="text-red-500" aria-label={t('admin.acc_match_dir_out')} />}
            <span className={`font-bold text-xs ${isIn ? 'text-green-600' : 'text-red-600'}`}>
              {fmtTRY(v)}
            </span>
          </div>
        );
      },
    },
    {
      key: 'bank_name',
      label: t('admin.acc_match_col_bank'),
      render: v => <span className="text-xs">{v}</span>,
    },
    {
      key: 'description',
      label: t('admin.acc_match_col_desc'),
      render: v => <span className="text-xs text-[var(--text-muted)] truncate max-w-[180px] block">{v || '—'}</span>,
    },
    {
      key: 'suggested_payment_ref',
      label: t('admin.acc_match_col_suggested'),
      render: (v, row) => {
        const ref = v || row.suggested_payment_id;
        const hasConfidence = row.match_confidence !== undefined && row.match_confidence !== null;
        return (
          <div className="flex flex-col gap-1 min-w-0">
            {/* Güven skoru rozeti — FIX FAZA-3 */}
            {hasConfidence && (
              <ConfidenceBadge
                confidence={row.match_confidence}
                method={row.match_method}
                t={t}
              />
            )}
            {/* Önerilen ödeme referansı */}
            {ref ? (
              <span
                title={ref}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-[10px] font-bold cursor-default truncate max-w-[160px]"
              >
                <Info size={10} aria-hidden="true" />
                {ref.length > 20 ? `${ref.slice(0, 20)}…` : ref}
              </span>
            ) : !hasConfidence ? (
              <span className="text-xs text-[var(--text-muted)]">—</span>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'actions',
      label: t('admin.acc_match_col_actions'),
      render: (_v, row) => (
        <div className="flex gap-1">
          <button
            onClick={() => setMatchModal(row)}
            className="px-3 py-1.5 rounded-xl bg-[var(--brand-primary)] text-white text-xs font-bold hover:opacity-90 min-h-[36px] flex items-center gap-1"
          >
            <Link2 size={12} />
            {t('admin.acc_match_btn_match')}
          </button>
          <button
            onClick={() => deleteBankTx(row.transaction_id)}
            className="p-1.5 rounded bg-red-50 text-red-500 hover:bg-red-100 min-h-[36px]"
            aria-label={t('admin.acc_match_btn_delete')}
          >
            <Trash2 size={14} />
          </button>
        </div>
      ),
    },
  ];

  // ── Filtered data ──────────────────────────────────────────────────────────
  // installments/overdue/notes/expenses/refunds artık sunucu taraflı `q` ile
  // filtreleniyor (useAccountingList) — client-side filteredXxx dizileri
  // kaldırıldı, tabların render'ı doğrudan instList.rows/overdueList.rows/
  // notesList.rows/expensesList.rows/refundsList.rows kullanıyor.
  const filteredBankTxs = bankTxSearch
    ? bankTxs.filter(tx =>
        tx.sender_name?.toLowerCase().includes(bankTxSearch.toLowerCase()) ||
        tx.description?.toLowerCase().includes(bankTxSearch.toLowerCase()) ||
        tx.bank_name?.toLowerCase().includes(bankTxSearch.toLowerCase()) ||
        tx.ext_ref?.toLowerCase().includes(bankTxSearch.toLowerCase())
      )
    : bankTxs;

  return (
    <AdminLayout title={t('admin.acc_page_title')} titleTr={t('admin.acc_page_title')}>
      <PageHeader
        title={t('admin.acc_page_title')}
        subtitle={t('admin.acc_page_subtitle')}
        breadcrumbs={[{ label: t('admin.acc_breadcrumb_admin'), href: '/admin' }, { label: t('admin.acc_page_title') }]}
      />

      {/* AKIŞ kartlarının bağlı olduğu seçili tarih aralığı — kartların üstünde */}
      {overviewSummarySearched && overviewAppliedRange && (
        <p className="text-[11px] font-semibold text-[var(--text-muted)] mb-2">
          {t('admin.acc_overview_range_label')}: {fmtDate(overviewAppliedRange.from)} – {fmtDate(overviewAppliedRange.to)}
        </p>
      )}

      {/* KPI cards — tıklanabilir (FIX 3) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-9 gap-3 mb-6">
        {kpis.map((kpi, i) => {
          const clickable = !!kpi.targetTab;
          const Tag = clickable ? 'button' : 'div';
          // AKIŞ kartları (kpi.flow) "Listele" tıklanana kadar rakam yerine
          // ipucu gösterir — eski tüm-zamanlar rakamı YANLIŞ YORUMLANIR diye
          // burada hiç render edilmiyor (bkz. yukarıdaki overviewFlowReady yorumu).
          const showFlowHint = kpi.flow && !overviewSummarySearched;
          const showFlowLoading = kpi.flow && overviewSummarySearched && overviewSummaryLoading;
          return (
            <Tag
              key={i}
              className={`card-clay p-3 text-center w-full ${clickable ? 'cursor-pointer hover:shadow-md hover:scale-[1.02] transition-transform focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:ring-offset-1' : ''}`}
              aria-label={showFlowHint ? `${kpi.label}: ${t('admin.acc_list_date_required')}` : `${kpi.label}: ${kpi.value}${clickable ? ` — ${t('admin.acc_kpi_go_tab')}` : ''}`}
              tabIndex={clickable ? 0 : undefined}
              onClick={clickable ? () => setTab(kpi.targetTab) : undefined}
              onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTab(kpi.targetTab); } } : undefined}
            >
              <kpi.icon size={18} className="mx-auto mb-1" style={{ color: kpi.color }} />
              {showFlowHint ? (
                <p className="text-[10px] font-semibold text-[var(--text-muted)] leading-tight normal-case">
                  {t('admin.acc_list_date_required')}
                </p>
              ) : (
                <p className="font-display text-base font-bold leading-tight">
                  {showFlowLoading ? <RefreshCw size={14} className="animate-spin mx-auto" /> : kpi.value}
                </p>
              )}
              <p className="text-[9px] text-[var(--text-muted)] font-semibold mt-0.5 leading-snug">{kpi.label}</p>
            </Tag>
          );
        })}
      </div>

      {/* Tabs — #6: role="tab" + aria-selected + aria-controls */}
      <div role="tablist" className="flex gap-1 mb-6 overflow-x-auto pb-1">
        {tabConfig.map(tc => (
          <button
            key={tc.id}
            role="tab"
            aria-selected={tab === tc.id}
            aria-controls={`acc-panel-${tc.id}`}
            id={`acc-tab-${tc.id}`}
            onClick={() => setTab(tc.id)}
            className={`px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
              tab === tc.id
                ? 'bg-[var(--brand-primary)] text-white'
                : 'bg-white border-2 border-[var(--border-light)] hover:border-[var(--brand-primary)]'
            }`}
            data-testid={`acc-tab-${tc.id}`}
          >
            {tc.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ─────────────────────────────────────────────────────────── */}
      {tab === 'overview' && (
        <div id="acc-panel-overview" role="tabpanel" aria-labelledby="acc-tab-overview" tabIndex={-1}>
          {/* AKIŞ kartlarını besleyen tarih aralığı — diğer 6 sekmedeki
              ListToolbar/useAccountingList deseniyle aynı fikir. Arama kutusu
              YOK: /admin/accounting/lists/summary bir agregat dönüyor (items/
              total sayfalama yok), aranacak bir satır listesi yok — bu yüzden
              ListToolbar bileşeni (search zorunlu prop) burada REUSE edilmedi,
              aynı DateRange bileşeni + aynı buton stiliyle sade bir blok yazıldı. */}
          <div className="card-clay p-4 mb-4">
            <h4 className="font-bold text-sm mb-3">{t('admin.acc_overview_flow_title')}</h4>
            <form
              onSubmit={(e) => { e.preventDefault(); handleListOverview(); }}
              className="flex flex-wrap items-end gap-3"
            >
              <div className="min-w-[240px]">
                <p className="text-[11px] font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1.5">{t('admin.acc_list_date_range')}</p>
                <DateRange value={overviewDateRange} onChange={setOverviewDateRange} shortcuts={['today', 'last7', 'last30', 'thisMonth', 'lastMonth']} />
              </div>
              <button
                type="submit"
                disabled={overviewSummaryLoading || !(overviewDateRange?.from && overviewDateRange?.to)}
                className="btn-primary h-9 px-5 text-xs flex items-center gap-1.5 disabled:opacity-50"
              >
                {overviewSummaryLoading ? <RefreshCw size={14} className="animate-spin" /> : <Search size={14} />}
                {t('admin.acc_list_btn_list')}
              </button>
              {!(overviewDateRange?.from && overviewDateRange?.to) && (
                <p className="text-[11px] font-semibold text-amber-600 flex items-center gap-1 basis-full">
                  <AlertTriangle size={13} className="shrink-0" />
                  {t('admin.acc_list_date_required')}
                </p>
              )}
            </form>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
          <div className="card-clay p-4">
            <h4 className="font-bold text-sm mb-3 flex items-center gap-2">
              <Landmark size={16} className="text-[var(--brand-primary)]" />
              {t('admin.acc_banks')}
            </h4>
            {banks.map(b => {
              const bBalance = b.balance || 0;
              return (
                <div key={b.bank_id} className="flex justify-between items-center py-2 border-b border-[var(--border-light)] text-sm">
                  <span className="font-semibold">{b.name}</span>
                  <span className="flex items-center gap-1.5">
                    {bBalance < 0 && (
                      <span className="text-[9px] font-bold uppercase text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded">{t('admin.acc_balance_deficit_badge')}</span>
                    )}
                    <span className={`font-bold ${balanceToneClass(bBalance)}`}>{fmtTRY(bBalance)}</span>
                  </span>
                </div>
              );
            })}
            {banks.length === 0 && <p className="text-xs text-[var(--text-muted)] text-center py-3">{t('admin.acc_no_banks')}</p>}
          </div>
          <div className="card-clay p-4">
            <h4 className="font-bold text-sm mb-3 flex items-center gap-2">
              <AlertTriangle size={16} className="text-[var(--brand-danger)]" />
              {t('admin.acc_overdue_label')} ({overduePreview.total})
            </h4>
            {/* Düzeltme turu: bu widget Overdue TAB'inin `overdueList`'inden
                (Listele bekleyen) DEĞİL, mount'ta çeken ayrı `overduePreview`'dan
                besleniyor — bkz. fetchOverduePreview üstündeki yorum. */}
            {overduePreview.rows.slice(0, 5).map(i => (
              <div key={i.installment_id} className="flex justify-between py-2 border-b border-[var(--border-light)] text-sm">
                <div>
                  <p className="font-semibold text-xs">{i.user_name}</p>
                  <p className="text-[10px] text-red-500">{t('admin.acc_col_due')}: {i.due_date}</p>
                </div>
                <span className="font-bold text-red-500">{fmtTRY(i.amount)}</span>
              </div>
            ))}
            {overduePreview.rows.length === 0 && (
              <div className="py-4 text-center">
                <Check size={24} className="mx-auto text-green-500 mb-1" />
                <p className="text-xs text-[var(--text-muted)] font-semibold">{t('admin.acc_no_overdue')}</p>
              </div>
            )}
          </div>
          </div>
        </div>
      )}

      {/* ── ENTRIES / BİRLEŞİK DEFTER ────────────────────────────────────────── */}
      {tab === 'entries' && (
        <div id="acc-panel-entries" role="tabpanel" aria-labelledby="acc-tab-entries" tabIndex={-1}>

          {ledgerFinalBalance !== null && (
            <div className={`card-clay px-4 py-2 mb-4 inline-flex items-center gap-2 border-l-4 ${
              ledgerFinalBalance > 0 ? 'border-l-[var(--brand-primary)]' : ledgerFinalBalance === 0 ? 'border-l-emerald-500' : 'border-l-rose-500'
            }`}>
              <DollarSign size={16} className={balanceToneClass(ledgerFinalBalance)} />
              <div>
                <p className="text-[10px] font-bold text-[var(--text-muted)] uppercase">{t('admin.acc_ledger_current_balance')}</p>
                <p className={`font-bold text-lg ${balanceToneClass(ledgerFinalBalance)}`}>{fmtTRY(ledgerFinalBalance)}</p>
                {/* FAZ 1 hizalama: negatif değer öğrenci alacağı DEĞİL — kurumun
                    kayıtlı çıkışları (gider+iade) kayıtlı girişleri aştığı
                    anlamına gelir. balance_creditor etiketini burada KULLANMA. */}
                {ledgerFinalBalance < 0 && (
                  <p className="text-[10px] text-rose-500 mt-0.5 max-w-[220px]">{t('admin.acc_ledger_balance_deficit_hint')}</p>
                )}
              </div>
            </div>
          )}

          {/* Tarih aralığı ZORUNLU — seçilmeden "Listele" pasif, ilk açılışta
              hiçbir sorgu atılmaz (feature/mali-isler-liste-web). */}
          <ListToolbar
            dateRange={ledgerDateRange}
            onDateRangeChange={setLedgerDateRange}
            search={ledgerSearch}
            onSearchChange={setLedgerSearch}
            onListele={handleListLedger}
            hasDateRange={Boolean(ledgerDateRange?.from && ledgerDateRange?.to)}
            loading={ledgerList.loading}
            t={t}
          />

          {ledgerList.truncated && <TruncatedWarning t={t} />}

          {ledgerList.loading ? (
            <LoadingState text={t('admin.acc_ledger_loading')} />
          ) : ledgerList.error ? (
            <div className="card-clay p-6 text-center text-red-500">
              <AlertTriangle size={32} className="mx-auto mb-2" />
              <p className="font-semibold text-sm">{t('admin.acc_ledger_error')}</p>
              <button onClick={ledgerList.refetchIfApplied} className="btn-secondary mt-3 px-4 py-2 text-xs">{t('admin.acc_retry')}</button>
            </div>
          ) : ledgerList.rows.length === 0 ? (
            <EmptyState
              icon={<BookOpen size={40} strokeWidth={1.5} />}
              title={t('admin.acc_ledger_empty')}
              description={ledgerList.hasSearched ? undefined : t('admin.acc_list_empty_no_range')}
            />
          ) : (
            <>
              <div className="overflow-x-auto rounded-2xl border-2 border-[var(--border-light)]">
                <table className="w-full text-xs" role="table">
                  <thead>
                    <tr className="bg-gray-50 text-[var(--text-muted)] uppercase tracking-wide text-[10px]">
                      <th className="px-3 py-2.5 text-left font-semibold">{t('admin.acc_ledger_col_date')}</th>
                      <th className="px-3 py-2.5 text-left font-semibold">{t('admin.acc_ledger_col_type')}</th>
                      <th className="px-3 py-2.5 text-left font-semibold">{t('admin.acc_ledger_col_desc')}</th>
                      <th className="px-3 py-2.5 text-right font-semibold">{t('admin.acc_ledger_col_debit')}</th>
                      <th className="px-3 py-2.5 text-right font-semibold">{t('admin.acc_ledger_col_credit')}</th>
                      <th className="px-3 py-2.5 text-right font-semibold">{t('admin.acc_ledger_col_amount')}</th>
                      <th className="px-3 py-2.5 text-right font-semibold">{t('admin.acc_ledger_col_balance')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerList.rows.map((e, idx) => (
                      <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="px-3 py-2 whitespace-nowrap text-[var(--text-muted)]">{fmtDate(e.date)}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold ${
                            ledgerTypeVariant(e.type) === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
                            ledgerTypeVariant(e.type) === 'danger' ? 'bg-red-50 text-red-700 border border-red-200' :
                            ledgerTypeVariant(e.type) === 'warning' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                            'bg-gray-50 text-gray-700 border border-gray-200'
                          }`}>{e.type || '—'}</span>
                        </td>
                        <td className="px-3 py-2 max-w-[240px] text-[var(--text-muted)] truncate">{e.description || '—'}</td>
                        <td className="px-3 py-2 text-right font-semibold text-red-600">
                          {e.direction === 'out' ? fmtTRY(e.amount) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-green-600">
                          {e.direction === 'in' ? fmtTRY(e.amount) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right font-bold">{fmtTRY(e.amount)}</td>
                        <td className={`px-3 py-2 text-right font-bold ${e.running_balance < 0 ? 'text-rose-600' : 'text-[var(--brand-primary)]'}`}>{fmtTRY(e.running_balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Sayfalama — sunucu taraflı (LIST_LIMIT=50, 1-indexed) */}
              {ledgerList.total > LIST_LIMIT && (
                <div className="flex items-center justify-center gap-3 mt-4 text-xs font-bold">
                  <button
                    onClick={() => ledgerList.setPage(p => Math.max(1, p - 1))}
                    disabled={ledgerList.page === 1}
                    aria-label={t('admin.acc_ledger_prev')}
                    className="p-1.5 rounded-lg border-2 border-[var(--border-light)] disabled:opacity-40 hover:border-[var(--brand-primary)] transition"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <span className="text-[var(--text-muted)]">
                    {ledgerList.page} / {Math.ceil(ledgerList.total / LIST_LIMIT)}
                  </span>
                  <button
                    onClick={() => ledgerList.setPage(p => p + 1)}
                    disabled={ledgerList.page * LIST_LIMIT >= ledgerList.total}
                    aria-label={t('admin.acc_ledger_next')}
                    className="p-1.5 rounded-lg border-2 border-[var(--border-light)] disabled:opacity-40 hover:border-[var(--brand-primary)] transition"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── BANKS ─────────────────────────────────────────────────────────────── */}
      {tab === 'banks' && (
        <div id="acc-panel-banks" role="tabpanel" aria-labelledby="acc-tab-banks" tabIndex={-1}>
          <div className="flex justify-end mb-4">
            <button onClick={() => setShowBankForm(true)} className="btn-primary px-4 py-2 text-xs flex items-center gap-1">
              <Plus size={14} /> {t('admin.acc_add_bank')}
            </button>
          </div>
          {showBankForm && (
            <div className="card-clay p-4 mb-4 space-y-3">
              <div className="flex justify-between">
                <h3 className="font-bold text-sm">{editingBank ? t('admin.acc_edit_bank') : t('admin.acc_new_bank')}</h3>
                <button onClick={closeBankForm} disabled={saving.bank}><X size={16} /></button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input placeholder={t('admin.acc_account_name')} value={bankForm.name} onChange={e => setBankForm({ ...bankForm, name: e.target.value })} className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm" data-testid="bank-name-input" />
                <div className="flex flex-col gap-1">
                  <label htmlFor="bank-account-type-select" className="text-[10px] font-bold text-[var(--text-muted)]">{t('admin.acc_account_type')}</label>
                  <select
                    id="bank-account-type-select"
                    value={bankForm.account_type}
                    onChange={e => setBankForm({ ...bankForm, account_type: e.target.value })}
                    className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm"
                    data-testid="bank-account-type-select"
                  >
                    <option value="bank">{t('admin.acc_account_type_bank')}</option>
                    <option value="cash">{t('admin.acc_account_type_cash')}</option>
                    <option value="pos">{t('admin.acc_account_type_pos')}</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label htmlFor="bank-currency-select" className="text-[10px] font-bold text-[var(--text-muted)]">{t('admin.acc_currency')}</label>
                  <select
                    id="bank-currency-select"
                    value={bankForm.currency}
                    onChange={e => setBankForm({ ...bankForm, currency: e.target.value })}
                    className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm"
                    data-testid="bank-currency-select"
                  >
                    <option value="TRY">TRY</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
                <input type="number" placeholder={t('admin.acc_opening_balance')} value={bankForm.balance} onChange={e => setBankForm({ ...bankForm, balance: parseFloat(e.target.value) || 0 })} className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm" />
                {bankForm.account_type !== 'cash' && (
                  <>
                    <input placeholder={t('admin.acc_iban_placeholder')} value={bankForm.iban} onChange={e => setBankForm({ ...bankForm, iban: e.target.value.toUpperCase() })} maxLength={32} className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm font-mono" data-testid="bank-iban-input" />
                    <input placeholder={t('admin.acc_account_no')} value={bankForm.account_no} onChange={e => setBankForm({ ...bankForm, account_no: e.target.value })} className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm" />
                    <input placeholder={t('admin.acc_branch')} value={bankForm.branch} onChange={e => setBankForm({ ...bankForm, branch: e.target.value })} className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm" />
                  </>
                )}
              </div>
              {bankForm.account_type === 'bank' && (
                <p className="text-[10px] text-[var(--text-muted)]">{t('admin.acc_iban_hint')}</p>
              )}
              {bankForm.account_type === 'pos' && (
                <p className="text-[10px] text-[var(--text-muted)]">{t('admin.acc_iban_hint_optional')}</p>
              )}
              <div className="flex gap-2">
                <button onClick={saveBank} disabled={saving.bank} className="btn-success px-6 py-2 text-xs disabled:opacity-50" data-testid="bank-save-btn">
                  {saving.bank ? t('admin.acc_saving') : editingBank ? t('admin.acc_update') : t('admin.acc_save')}
                </button>
                <button onClick={closeBankForm} disabled={saving.bank} className="px-6 py-2 text-xs rounded-xl border-2 border-[var(--border-light)] font-bold disabled:opacity-50">
                  {t('admin.btn_cancel')}
                </button>
              </div>
            </div>
          )}
          {banks.length === 0
            ? <EmptyState icon={<Landmark size={40} strokeWidth={1.5} />} title={t('admin.acc_no_banks')} />
            : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {banks.map(b => {
                  const AccountTypeIcon = accountTypeIcon(b.account_type);
                  return (
                  <div
                    key={b.bank_id}
                    className="card-clay p-5 cursor-pointer hover:shadow-md hover:scale-[1.01] transition-transform focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                    role="button"
                    tabIndex={0}
                    aria-label={`${b.name} — ${t('admin.acc_statement_open')}`}
                    onClick={() => setStatementBank({ bank_id: b.bank_id, name: b.name })}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setStatementBank({ bank_id: b.bank_id, name: b.name }); } }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <AccountTypeIcon size={20} className="text-[var(--brand-primary)]" />
                      <h4 className="font-bold flex-1 min-w-0 truncate">{b.name}</h4>
                      <button
                        onClick={e => { e.stopPropagation(); openEditBank(b); }}
                        className="p-1.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100"
                        data-testid={`edit-bank-${b.bank_id}`}
                        aria-label={t('admin.acc_edit_bank')}
                      >
                        <Pencil size={14} />
                      </button>
                    </div>
                    <div className="mb-2">
                      <StatusBadge variant={accountTypeVariant(b.account_type)} label={accountTypeLabel(b.account_type, t)} />
                    </div>
                    <p className="font-display text-2xl font-bold text-[var(--brand-primary)] mb-2">{fmtTRY(b.balance || 0)}</p>
                    <div className="space-y-1 text-xs text-[var(--text-muted)]">
                      {b.account_type !== 'cash' && b.iban && <p>IBAN: <span className="font-mono">{b.iban}</span></p>}
                      {b.account_type !== 'cash' && b.account_no && <p>{t('admin.acc_account_no')}: {b.account_no}</p>}
                      {b.account_type !== 'cash' && b.branch && <p>{t('admin.acc_branch')}: {b.branch}</p>}
                    </div>
                    <p className="text-[10px] text-[var(--brand-primary)] font-bold mt-3 flex items-center gap-1">
                      <FileText size={10} />
                      {t('admin.acc_statement_open')}
                    </p>
                  </div>
                  );
                })}
              </div>
            )
          }

          {/* Bank Statement Modal — FIX 2 */}
          {statementBank && (
            <BankStatementModal
              bankId={statementBank.bank_id}
              bankName={statementBank.name}
              authHeaders={authHeaders}
              t={t}
              toast={toast}
              lang={lang}
              onClose={() => setStatementBank(null)}
            />
          )}
        </div>
      )}

      {/* ── INSTALLMENTS ─────────────────────────────────────────────────────── */}
      {tab === 'installments' && (
        <div id="acc-panel-installments" role="tabpanel" aria-labelledby="acc-tab-installments" tabIndex={-1}>
          <ListToolbar
            dateRange={instDateRange}
            onDateRangeChange={setInstDateRange}
            search={installmentSearch}
            onSearchChange={setInstallmentSearch}
            onListele={handleListInstallments}
            hasDateRange={Boolean(instDateRange?.from && instDateRange?.to)}
            loading={instList.loading}
            t={t}
          />
          {instList.truncated && <TruncatedWarning t={t} />}
          <DataTable
            columns={installmentCols}
            data={instList.rows}
            loading={instList.loading}
            rowKey="installment_id"
            pageSize={LIST_LIMIT}
            emptyIcon={<CreditCard size={40} strokeWidth={1.5} />}
            emptyTitle={t('admin.acc_no_installments')}
            emptyDescription={instList.hasSearched ? undefined : t('admin.acc_list_empty_no_range')}
            serverPagination
            currentPage={instList.page}
            totalItems={instList.total}
            onPageChange={instList.setPage}
          />
        </div>
      )}

      {/* ── OVERDUE ───────────────────────────────────────────────────────────── */}
      {/* Karar (feature/mali-isler-liste-web): önceden sayfalamasız kart listesiydi
          — sunucu taraflı sayfalama zorunluluğuyla birlikte diğer 5 liste
          sekmesiyle tutarlı olması için DataTable'a geçirildi (proje-yoneticisi
          talimatı: "daha tutarlı olanı seç, raporla"). */}
      {tab === 'overdue' && (
        <div id="acc-panel-overdue" role="tabpanel" aria-labelledby="acc-tab-overdue" tabIndex={-1}>
          <ListToolbar
            dateRange={overdueDateRange}
            onDateRangeChange={setOverdueDateRange}
            search={overdueSearch}
            onSearchChange={setOverdueSearch}
            onListele={handleListOverdue}
            hasDateRange={Boolean(overdueDateRange?.from && overdueDateRange?.to)}
            loading={overdueList.loading}
            t={t}
          />
          {overdueList.truncated && <TruncatedWarning t={t} />}
          {overdueList.rows.length > 0 && (
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-4 flex items-center gap-3">
              <AlertTriangle size={24} className="text-red-500" />
              <p className="font-bold text-sm text-red-600">{overdueList.total} {t('admin.acc_overdue_label').toLowerCase()}</p>
            </div>
          )}
          <DataTable
            columns={overdueCols}
            data={overdueList.rows}
            loading={overdueList.loading}
            rowKey="installment_id"
            pageSize={LIST_LIMIT}
            emptyIcon={<Check size={40} strokeWidth={1.5} />}
            emptyTitle={t('admin.acc_no_overdue')}
            emptyDescription={overdueList.hasSearched ? undefined : t('admin.acc_list_empty_no_range')}
            serverPagination
            currentPage={overdueList.page}
            totalItems={overdueList.total}
            onPageChange={overdueList.setPage}
          />
        </div>
      )}

      {/* ── PROMISSORY NOTES ─────────────────────────────────────────────────── */}
      {tab === 'notes' && (
        <div id="acc-panel-notes" role="tabpanel" aria-labelledby="acc-tab-notes" tabIndex={-1}>
          <div className="flex justify-end mb-4">
            <button onClick={() => setShowNoteForm(true)} className="btn-primary px-4 py-2 text-xs flex items-center gap-1">
              <Plus size={14} /> {t('admin.acc_add_note')}
            </button>
          </div>
          {showNoteForm && (
            <div className="card-clay p-4 mb-4 space-y-3">
              <div className="flex justify-between">
                <h3 className="font-bold text-sm">{t('admin.acc_add_note')}</h3>
                <button onClick={() => setShowNoteForm(false)}><X size={16} /></button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {/* Öğrenci seçimi Tur 3'te (feature/mali-isler-ozet-tarih-web) ortak
                    StudentSearchCombobox'a çevrildi — dosya başındaki yorum bloğuna bkz. */}
                <StudentSearchCombobox
                  id="note-user-search"
                  value={noteForm.user_id}
                  onChange={(userId) => setNoteForm({ ...noteForm, user_id: userId })}
                  placeholder={t('admin.acc_select_student')}
                />
                <input placeholder={t('admin.acc_note_number')} value={noteForm.note_number} onChange={e => setNoteForm({ ...noteForm, note_number: e.target.value })} className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm" />
                <input type="number" placeholder={t('admin.acc_col_amount')} value={noteForm.amount} onChange={e => setNoteForm({ ...noteForm, amount: parseFloat(e.target.value) || 0 })} className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm" />
                <input type="date" value={noteForm.due_date} onChange={e => setNoteForm({ ...noteForm, due_date: e.target.value })} className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm" />
              </div>
              {/* A0-3 fix: fatura seçimi zorunlu — öğrenci seçilince faturaları listele */}
              <div>
                <label htmlFor="note-invoice-id" className="text-xs font-bold text-[var(--text-main)] block mb-0.5">
                  {t('admin.acc_note_invoice_id')} <span className="text-red-500">*</span>
                </label>
                {loadingNoteInvoices ? (
                  <div className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 text-sm text-[var(--text-muted)] flex items-center gap-2">
                    <RefreshCw size={12} className="animate-spin shrink-0" />
                    {t('admin.acc_note_invoice_loading')}
                  </div>
                ) : (
                  <select
                    id="note-invoice-id"
                    value={noteForm.invoice_id}
                    onChange={e => setNoteForm({ ...noteForm, invoice_id: e.target.value })}
                    disabled={!noteForm.user_id}
                    className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {!noteForm.user_id ? (
                      <option value="">{t('admin.acc_note_invoice_select_student_first')}</option>
                    ) : noteInvoices.length === 0 ? (
                      <option value="">{t('admin.acc_note_invoice_none')}</option>
                    ) : (
                      <>
                        <option value="">{t('admin.acc_note_invoice_select')}</option>
                        {noteInvoices.map(inv => (
                          <option key={inv.invoice_id} value={inv.invoice_id}>
                            {inv.invoice_no} — {fmtTRY(inv.grand_total ?? inv.total_amount)} ({t('admin.acc_refund_col_amount').toLowerCase()}: {fmtTRY(inv.remaining_amount)}) — {inv.status}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                )}
              </div>
              <input placeholder={t('admin.acc_col_desc')} value={noteForm.description} onChange={e => setNoteForm({ ...noteForm, description: e.target.value })} className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm" />
              <button onClick={saveNote} disabled={saving.note || !noteForm.invoice_id} className="btn-success px-6 py-2 text-xs disabled:opacity-50">
                {saving.note ? t('admin.acc_creating') : t('admin.acc_create')}
              </button>
            </div>
          )}
          <ListToolbar
            dateRange={notesDateRange}
            onDateRangeChange={setNotesDateRange}
            search={noteSearch}
            onSearchChange={setNoteSearch}
            onListele={handleListNotes}
            hasDateRange={Boolean(notesDateRange?.from && notesDateRange?.to)}
            loading={notesList.loading}
            t={t}
          />
          {notesList.truncated && <TruncatedWarning t={t} />}
          <DataTable
            columns={noteCols}
            data={notesList.rows}
            loading={notesList.loading}
            rowKey="note_id"
            pageSize={LIST_LIMIT}
            emptyIcon={<FileText size={40} strokeWidth={1.5} />}
            emptyTitle={t('admin.acc_no_notes')}
            emptyDescription={notesList.hasSearched ? undefined : t('admin.acc_list_empty_no_range')}
            serverPagination
            currentPage={notesList.page}
            totalItems={notesList.total}
            onPageChange={notesList.setPage}
          />
        </div>
      )}

      {/* ── EXPENSES ─────────────────────────────────────────────────────────── */}
      {tab === 'expenses' && (
        <div id="acc-panel-expenses" role="tabpanel" aria-labelledby="acc-tab-expenses" tabIndex={-1}>
          <div className="flex justify-end mb-4">
            <button onClick={() => { setEditingExpense(null); setShowExpenseForm(true); }} className="btn-primary px-4 py-2 text-xs flex items-center gap-1">
              <Plus size={14} /> {t('admin.acc_expense_new')}
            </button>
          </div>

          {showExpenseForm && (
            <ExpenseForm
              initial={editingExpense ? {
                category: editingExpense.category || 'other',
                description: editingExpense.description || '',
                amount: editingExpense.amount || 0,
                date: editingExpense.date || TODAY,
                bank_id: editingExpense.bank_id || '',
                vendor: editingExpense.vendor || '',
                document_ref: editingExpense.document_ref || '',
                is_recurring: editingExpense.is_recurring || false,
              } : null}
              banks={banks}
              saving={saving.expense}
              onSave={saveExpense}
              onClose={() => { setShowExpenseForm(false); setEditingExpense(null); }}
              t={t}
            />
          )}

          {/* Category summary */}
          {expenseSummary.length > 0 && (
            <div className="card-clay p-4 mb-4">
              <h4 className="font-bold text-sm mb-3 flex items-center gap-2">
                <TrendingDown size={16} className="text-red-500" />
                {t('admin.acc_expense_summary')}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {expenseSummary.map((s) => (
                  <div key={s.category} className="text-center p-2 rounded-xl bg-red-50 border border-red-100">
                    <p className="text-xs font-bold">{EXPENSE_CATEGORIES_MAP[s.category] || s.category}</p>
                    <p className="font-bold text-red-600 text-sm">{fmtTRY(s.total || s.amount || 0)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tarih aralığı + arama + kategori — "Listele" ile sunucudan çekilir */}
          <ListToolbar
            dateRange={expensesDateRange}
            onDateRangeChange={setExpensesDateRange}
            search={expenseSearch}
            onSearchChange={setExpenseSearch}
            onListele={handleListExpenses}
            hasDateRange={Boolean(expensesDateRange?.from && expensesDateRange?.to)}
            loading={expensesList.loading}
            t={t}
          >
            <select
              value={expenseCategoryFilter}
              onChange={e => setExpenseCategoryFilter(e.target.value)}
              className="px-3 h-9 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-xs"
            >
              <option value="">{t('admin.acc_expense_category')}</option>
              {Object.entries(EXPENSE_CATEGORIES_MAP).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </ListToolbar>

          {expensesList.truncated && <TruncatedWarning t={t} />}

          {/* #9: loading/empty'den AYRI error state */}
          {expensesList.error
            ? <div className="card-clay p-6 text-center text-red-500"><AlertTriangle size={32} className="mx-auto mb-2" /><p className="font-semibold text-sm">{t('admin.acc_expense_load_error')}</p><button onClick={expensesList.refetchIfApplied} className="btn-secondary mt-3 px-4 py-2 text-xs">{t('admin.acc_retry')}</button></div>
            : (
              <DataTable
                columns={expenseCols}
                data={expensesList.rows}
                loading={expensesList.loading}
                rowKey="expense_id"
                pageSize={LIST_LIMIT}
                emptyIcon={<TrendingDown size={40} strokeWidth={1.5} />}
                emptyTitle={t('admin.acc_no_expenses')}
                emptyDescription={expensesList.hasSearched ? undefined : t('admin.acc_list_empty_no_range')}
                serverPagination
                currentPage={expensesList.page}
                totalItems={expensesList.total}
                onPageChange={expensesList.setPage}
              />
            )
          }
        </div>
      )}

      {/* ── REFUNDS ──────────────────────────────────────────────────────────── */}
      {tab === 'refunds' && (
        <div id="acc-panel-refunds" role="tabpanel" aria-labelledby="acc-tab-refunds" tabIndex={-1}>
          <div className="flex justify-end mb-4">
            <button onClick={() => setShowRefundForm(true)} className="btn-primary px-4 py-2 text-xs flex items-center gap-1">
              <Plus size={14} /> {t('admin.acc_refund_new')}
            </button>
          </div>

          {showRefundForm && (
            <RefundForm
              banks={banks}
              saving={saving.refund}
              onSave={saveRefund}
              onClose={() => setShowRefundForm(false)}
              t={t}
              authHeaders={authHeaders}
              toast={toast}
              lang={lang}
            />
          )}

          {/* Tarih aralığı + arama + durum/yöntem — "Listele" ile sunucudan çekilir */}
          <ListToolbar
            dateRange={refundsDateRange}
            onDateRangeChange={setRefundsDateRange}
            search={refundSearch}
            onSearchChange={setRefundSearch}
            onListele={handleListRefunds}
            hasDateRange={Boolean(refundsDateRange?.from && refundsDateRange?.to)}
            loading={refundsList.loading}
            t={t}
          >
            <select
              value={refundStatusFilter}
              onChange={e => setRefundStatusFilter(e.target.value)}
              className="px-3 h-9 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-xs"
            >
              <option value="">{t('admin.acc_refund_col_status')}</option>
              <option value="requested">{t('admin.acc_refund_status_requested')}</option>
              <option value="approved">{t('admin.acc_refund_status_approved')}</option>
              <option value="completed">{t('admin.acc_refund_status_completed')}</option>
              <option value="rejected">{t('admin.acc_refund_status_rejected')}</option>
            </select>
            <select
              value={refundMethodFilter}
              onChange={e => setRefundMethodFilter(e.target.value)}
              className="px-3 h-9 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-xs"
            >
              <option value="">{t('admin.acc_refund_col_method')}</option>
              <option value="transfer">{t('admin.acc_method_transfer')}</option>
              <option value="cash">{t('admin.acc_method_cash')}</option>
              <option value="moka_refund">{t('admin.acc_method_moka_refund')}</option>
            </select>
          </ListToolbar>

          {refundsList.truncated && <TruncatedWarning t={t} />}

          {/* #9: loading/empty'den AYRI error state */}
          {refundsList.error
            ? <div className="card-clay p-6 text-center text-red-500"><AlertTriangle size={32} className="mx-auto mb-2" /><p className="font-semibold text-sm">{t('admin.acc_refund_load_error')}</p><button onClick={refundsList.refetchIfApplied} className="btn-secondary mt-3 px-4 py-2 text-xs">{t('admin.acc_retry')}</button></div>
            : (
              <DataTable
                columns={refundCols}
                data={refundsList.rows}
                loading={refundsList.loading}
                rowKey="refund_id"
                pageSize={LIST_LIMIT}
                emptyIcon={<RotateCcw size={40} strokeWidth={1.5} />}
                emptyTitle={t('admin.acc_no_refunds')}
                emptyDescription={refundsList.hasSearched ? undefined : t('admin.acc_list_empty_no_range')}
                serverPagination
                currentPage={refundsList.page}
                totalItems={refundsList.total}
                onPageChange={refundsList.setPage}
              />
            )
          }
        </div>
      )}

      {/* ── MATCH POOL ───────────────────────────────────────────────────────── */}
      {tab === 'match_pool' && (
        <div id="acc-panel-match_pool" role="tabpanel" aria-labelledby="acc-tab-match_pool" tabIndex={-1}>
          {/* Toolbar */}
          <div className="flex gap-2 mb-4 flex-wrap">
            <FilterBar
              search={bankTxSearch}
              onSearchChange={setBankTxSearch}
              searchPlaceholder={t('admin.search_placeholder')}
              className="flex-1 min-w-48"
            />
            {/* FIX FAZA-3: Otomatik Eşleştir */}
            <button
              onClick={runAutoMatch}
              disabled={saving.autoMatch}
              className="px-4 py-2 text-xs flex items-center gap-1 rounded-xl border-2 font-bold border-emerald-400 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors disabled:opacity-50"
              aria-label={t('admin.acc_auto_match_btn')}
            >
              <RefreshCw size={14} className={saving.autoMatch ? 'animate-spin' : ''} aria-hidden="true" />
              {saving.autoMatch ? t('admin.acc_auto_match_running') : t('admin.acc_auto_match_btn')}
            </button>
            <button
              onClick={() => { setShowHavaleForm(v => !v); setShowBankTxForm(false); setShowCsvPanel(false); setCsvResult(null); if (showHavaleForm) setHavaleResult(null); }}
              className="px-4 py-2 text-xs flex items-center gap-1 rounded-xl border-2 font-bold border-cyan-400 text-cyan-700 bg-cyan-50 hover:bg-cyan-100 transition-colors"
            >
              <Link2 size={14} /> {t('admin.acc_havale_btn')}
            </button>
            <button
              onClick={() => { setShowBankTxForm(v => !v); setShowCsvPanel(false); setCsvResult(null); setShowHavaleForm(false); }}
              className="btn-primary px-4 py-2 text-xs flex items-center gap-1"
            >
              <Plus size={14} /> {t('admin.acc_match_new_tx')}
            </button>
            <button
              onClick={() => { setShowCsvPanel(v => !v); setShowBankTxForm(false); setCsvResult(null); setShowHavaleForm(false); }}
              className="btn-secondary px-4 py-2 text-xs flex items-center gap-1"
            >
              <Upload size={14} /> {t('admin.acc_match_csv_import')}
            </button>
          </div>

          {/* Havale talebi formu */}
          {showHavaleForm && (
            <HavaleRequestForm
              banks={banks}
              saving={saving.havale}
              onSave={saveHavale}
              onClose={() => { setShowHavaleForm(false); }}
              t={t}
            />
          )}

          {/* Havale talebi sonucu */}
          {havaleResult && (
            <HavaleResult
              result={havaleResult}
              onClose={() => setHavaleResult(null)}
              t={t}
            />
          )}

          {/* New tx form */}
          {showBankTxForm && (
            <BankTxForm
              saving={saving.banktx}
              onSave={saveBankTx}
              onClose={() => setShowBankTxForm(false)}
              t={t}
              banks={banks}
            />
          )}

          {/* CSV panel */}
          {showCsvPanel && (
            <div className="card-clay p-4 mb-4 space-y-3">
              <div className="flex justify-between items-center">
                <h3 className="font-bold text-sm flex items-center gap-2">
                  <Upload size={16} className="text-[var(--brand-primary)]" />
                  {t('admin.acc_match_csv_title')}
                </h3>
                <button onClick={() => { setShowCsvPanel(false); setCsvResult(null); }} aria-label={t('admin.btn_close')} className="p-1 rounded hover:bg-gray-100">
                  <X size={16} />
                </button>
              </div>
              <p className="text-xs text-[var(--text-muted)]">{t('admin.acc_match_csv_hint')}</p>
              <div>
                <label htmlFor="csv-file-input" className="text-xs font-bold block mb-1">CSV</label>
                <input
                  id="csv-file-input"
                  type="file"
                  accept=".csv"
                  onChange={e => { setCsvFile(e.target.files[0] || null); setCsvResult(null); }}
                  className="text-xs"
                />
              </div>
              {csvResult && (
                <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-3 text-xs">
                  <p className="font-bold text-emerald-700">{csvResult.msg}</p>
                  {csvResult.error_details?.length > 0 && (
                    <details className="mt-2">
                      <summary className="cursor-pointer font-semibold text-red-600">{t('admin.acc_match_csv_error_details')}</summary>
                      <ul className="mt-1 space-y-0.5">
                        {csvResult.error_details.map((e, i) => (
                          <li key={i} className="text-red-500">{typeof e === 'string' ? e : JSON.stringify(e)}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={importCsv}
                  disabled={!csvFile || saving.csvImporting}
                  className="btn-success px-4 py-1.5 text-xs disabled:opacity-50 flex items-center gap-1"
                >
                  <Upload size={12} />
                  {saving.csvImporting ? t('admin.acc_match_csv_importing') : t('admin.acc_match_csv_btn')}
                </button>
              </div>
            </div>
          )}

          {/* Table */}
          {loadingBankTxs
            ? <LoadingState text={t('admin.acc_match_loading')} />
            : errorBankTxs
              ? (
                <div className="card-clay p-6 text-center text-red-500">
                  <AlertTriangle size={32} className="mx-auto mb-2" />
                  <p className="font-semibold text-sm">{t('admin.acc_match_load_error')}</p>
                  <button onClick={fetchBankTxs} className="btn-secondary mt-3 px-4 py-2 text-xs">
                    {t('admin.acc_retry')}
                  </button>
                </div>
              )
              : filteredBankTxs.length === 0
                ? (
                  <EmptyState
                    icon={<Link2 size={40} strokeWidth={1.5} />}
                    title={t('admin.acc_match_no_records')}
                    sub={t('admin.acc_match_no_records_desc')}
                  />
                )
                : (
                  <>
                    <DataTable columns={bankTxCols} data={filteredBankTxs} rowKey="transaction_id" pageSize={25} />
                    {bankTxsHasMore && (
                      <div className="mt-3 flex items-center gap-2 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2">
                        <AlertTriangle size={14} className="shrink-0" />
                        {t('admin.acc_match_has_more')}
                      </div>
                    )}
                  </>
                )
          }

          {/* FIX FAZA-3: Banka Connector Durum Paneli */}
          <BankConnectorPanel
            t={t}
            authHeaders={authHeaders}
            toast={toast}
            lang={lang}
          />
        </div>
      )}

      {/* ── INTEREST SETTINGS ────────────────────────────────────────────────── */}
      {tab === 'interest' && (
        <div id="acc-panel-interest" role="tabpanel" aria-labelledby="acc-tab-interest" tabIndex={-1}>
          <InterestSettingsPanel
            t={t}
            authHeaders={authHeaders}
            toast={toast}
            lang={lang}
          />
        </div>
      )}

      {/* Collect installment modal */}
      {collectModal && (
        <CollectModal
          installment={collectModal}
          banks={banks}
          saving={saving.collect}
          onCollect={doCollectInstallment}
          onClose={() => setCollectModal(null)}
          t={t}
        />
      )}

      {/* Collect promissory note modal — A0-5: bank_id zorunlu */}
      {noteCollectTarget && (
        <NoteCollectModal
          note={noteCollectTarget}
          banks={banks}
          saving={saving.noteCollect}
          onCollect={doCollectNote}
          onClose={() => setNoteCollectTarget(null)}
          t={t}
        />
      )}

      {/* Moka ödeme linki dialog'u */}
      {mokaLinkModal && (
        <MokaLinkDialog
          installment={mokaLinkModal}
          banks={banks}
          onClose={() => setMokaLinkModal(null)}
          t={t}
        />
      )}

      {/* Match modal */}
      {matchModal && (
        <MatchModal
          tx={matchModal}
          matching={saving.matching}
          onMatch={matchTx}
          onClose={() => setMatchModal(null)}
          t={t}
        />
      )}

      {/* Confirm dialog */}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(o) => { if (!o) setConfirmOpen(false); }}
        title={confirmConfig.title}
        description={confirmConfig.desc}
        variant={confirmConfig.variant}
        confirmLabel={confirmConfig.variant === 'danger' ? t('admin.btn_delete') : t('admin.btn_confirm')}
        onConfirm={handleConfirm}
        loading={confirmLoading}
      />
    </AdminLayout>
  );
}
